/* DEMO ONLY — every figure in this file is INVENTED. Nothing here is a reading, a measurement or a
   catalogue value; it exists so the Intelligence and Customer 360 screens do not show empty tiles
   during a demo while UAT serves no data. Added 2026-09-21 for PLA-8828 and meant to be DELETED:
   remove this file and the handful of call sites named in `DEMO_FALLBACK_CALL_SITES` below and every
   trace is gone. Flip `DEMO_FALLBACK_ENABLED` to `false` to switch it off without deleting it.

   TENANT SCOPE, 2026-11-04. The paths that INVENT A READING — `withDemoFallback`, the two
   confidence-pill bypasses in `data/demoFallbackConfidenceGate.ts` and `withDemoNarrative` — now
   ask `demoFallbackAllowed` instead of the master switch, so they draw for the demo tenant alone and
   every other tenant sees the reading, the error or the unavailable state the wire actually sent.
   The module-scope section constants below and in the section files are NOT tenant-aware: they are
   evaluated at import time, before any tenant is known, and they draw decorative section content
   rather than a measurement.

   TWO RELAXATIONS, BOTH ON THE USER'S EXPLICIT INSTRUCTION AND BOTH ON 2026-09-21. Recorded rather
   than quietly applied, because each overturns a rule this file had written down and enforced:

     - `insufficient_data` and the other failure states became fillable, so the demo draws a body
       over a card the wire could not calculate. See `RESCUED_STATES` and the note at
       `DEMO_FALLBACK_TABLE` for what that does and does not reach.
     - CONFIDENCE became fillable. Until this date the file invented NO confidence score and NO
       confidence band anywhere, on the ground that confidence is governed by the Metrics Catalogue
       and G32 makes it the server's to publish; five values were deleted for breaking that rule and
       nine tiles were named-and-skipped for the same reason. The user asked for confidence readings
       on both screens for the demo, so the rule is relaxed: `DemoCardFallback.confidence` now
       supplies a card-level pill and four confidence TILES carry a figure again.

   What the relaxation does NOT reach, and what is therefore still never invented: a `reason_code`, a
   `calculation_status`, a `Why this result?` sentence, and a reading the platform WITHHELD rather
   than failed to produce (`permissionRestricted`, `consentRestricted`). A live confidence still wins
   over a demo one everywhere. `DEMO_FALLBACK_ENABLED = false` puts all of it back, unchanged. */

import { reasonCopyKey } from 'components/Intelligence/reasonKey';
import i18n from 'utils/i18n';

import { Customer360IssueSeverity } from '../customer360/constants';
import { TCustomerInsights } from '../i18n/TModules';
import {
  CardDataState,
  CardDataStateKind,
  CardDistribution,
  CardMetric,
  DistributionSegment,
  IntelligenceFixture
} from '../types';
import { ConfidenceBand } from './confidence';
import { DEMO_C360_TILE_VALUES } from './demoFallbackCustomer360';
import { DEMO_C360_PANEL_ROWS } from './demoFallbackCustomer360Rows';
import {
  DEMO_CREDIT_CARD_HOLDING_LAYOUT,
  DEMO_FINTECH_PANELS,
  DEMO_INSURANCE_PANELS,
  DEMO_MORTGAGE_LAYOUT,
  DEMO_POLICY_RENEWAL_LAYOUT,
  DEMO_TELCO_PANELS,
  NO_FINTECH_PANELS,
  NO_INSURANCE_PANELS,
  NO_CREDIT_CARD_HOLDING_LAYOUT,
  NO_MORTGAGE_LAYOUT,
  NO_POLICY_RENEWAL_LAYOUT,
  NO_TELCO_PANELS
} from './demoFallbackIndustry';
import { segmentName } from './entityLabel';
import { aggregateReadiness, readinessTileValue } from './productReadinessMetric';
import { ProductReadinessRow } from './productReadinessResult';

/**
 * The master switch. `false` restores the real behaviour exactly — every tile this file would have
 * filled goes back to empty and every card goes back to the state the wire put it in.
 */
export const DEMO_FALLBACK_ENABLED = true;

/**
 * The tenants this layer may invent a reading for. An ALLOWLIST rather than a denylist, so a tenant
 * nobody has thought about gets what the wire actually said — the real figure, or the real error or
 * unavailable state behind it. A tenant onboarded tomorrow is therefore honest by default instead of
 * demo-filled until someone remembers to exclude it. `digibank` is the demo tenant and the only
 * entry.
 */
export const DEMO_FALLBACK_TENANTS: string[] = ['digibank'];

/**
 * Whether invented readings may be drawn: the master switch above AND a tenant name that is both
 * known and allowlisted. An ABSENT name is not allowed — a caller that cannot say which tenant it is
 * rendering for falls to the wire's own answer, never to a demo one.
 */
export const demoFallbackAllowed = (tenantName?: string | null): boolean =>
  DEMO_FALLBACK_ENABLED && !!tenantName && DEMO_FALLBACK_TENANTS.indexOf(tenantName) !== -1;

/**
 * Everywhere this file is reached from, so removing it is a checklist and not a search.
 *
 * Two sibling FILES, deleted whole with this one:
 *   `data/demoFallbackCustomerInsights.ts`  — Layer 1 panel and rail row sets.
 *   `data/demoFallbackCustomer360.ts`       — Customer 360 tile figures.
 *   `data/demoFallbackCustomer360Rows.ts`   — Customer 360 panel and rail row sets.
 *   `data/demoFallbackConfidenceGate.ts`    — the Layer 1 card-confidence gate bypass.
 *
 * And the call sites:
 *   1. `data/assembleCard.ts`         — `withDemoFallback` on the assembled fixture.
 *   2. `sections/IndustrySection.tsx` — the four emptied banking panel constants, and the
 *                                        emptied `industryEvidenceInputs` chip map.
 *   3. `sections/CustomerBaseSection.tsx`        — 1 emptied chip row.
 *   4. `sections/ExperienceValueSection.tsx`     — 4 emptied CLV / driver constants, and card 1's
 *                                                  stand-in driver rails, and the demo
 *                                                  branch of `modelEvidenceRowsFor`.
 *   5. `sections/RewardsLoyaltySection.tsx`      — 3 emptied tier / reversal constants.
 *   6. `sections/PaymentsSection.tsx`            — 9 emptied data-quality / payment constants.
 *   7. `sections/GrowthOpportunitiesSection.tsx` — 7 emptied propensity / handoff constants.
 *   8. `customer360/sections/CustomerExperienceSection.tsx`   — 2 emptied `View More` row sets.
 *   9. `customer360/sections/FinancialHealthSection.tsx`      — 5 emptied `View More` row sets.
 *  10. `customer360/sections/GrowthOpportunitiesSection.tsx`  — 3 emptied `View More` row sets.
 *  11. `customer360/sections/PaymentInsightsSection.tsx`      — 3 emptied `View More` row sets.
 *  12. `customer360/sections/RewardsLoyaltySection.tsx`       — 1 emptied `View More` row set.
 *  13. `customer360/sections/RiskEvidenceSection.tsx`         — 1 emptied `View More` row set.
 *  14. `sections/BehaviourEngagementSection.tsx` — 1 emptied disengagement-driver rail.
 *  15. `components/InsightCardRenderer.tsx` — `withDemoCardConfidenceGate` around the catalogue's
 *                                        card-confidence gate. Removing it leaves
 *                                        `const publishesConfidence = showsConfidence`, which IS
 *                                        the governed behaviour; the gate itself is real-path code
 *                                        and is not part of this layer.
 *  16. `customer360/sections/CustomerProfileSection.tsx` — `withDemoCustomer360CardConfidenceGate`
 *                                        around the same catalogue gate, for `profile_summary`
 *                                        ALONE. Removing it leaves
 *                                        `customer360CardShowsConfidence(cardKey) && state.kind ===
 *                                        'ok'`, which IS the governed behaviour: that card has no
 *                                        `Yes` metric and draws no pill.
 *
 * Plus the TEST GUARDS. Each carries a hoisted `jest.mock` of this file forcing
 * `DEMO_FALLBACK_ENABLED: false` and `withDemoFallback` to identity, so the file keeps running the
 * REAL path — setup only, no assertion in any of them was touched. That matters beyond the demo:
 * several of these exist specifically to keep invented data out, and they only go on protecting
 * production after this layer is reverted if they were made FLAG-AWARE rather than adjusted.
 * Removing this layer means deleting the mock block from each, and nothing else:
 * `sections/__tests__/industrySection.test.tsx`, `sections/__tests__/industryLiveRings.test.tsx`,
 * `data/__tests__/assembleCard.test.ts`, `data/__tests__/liveCardSource.test.ts`,
 * `data/__tests__/productReadinessMetric.test.ts`,
 * `customer360/__tests__/useCustomer360CardData.test.ts`,
 * `customer360/sections/__tests__/CustomerExperienceSection.test.tsx`,
 * `customer360/sections/__tests__/growthOpportunitiesSection.test.tsx`,
 * `customer360/sections/__tests__/paymentInsightsSection.test.tsx` and
 * `customer360/sections/__tests__/sections4-6.test.tsx`.
 *
 * MEASURED 2026-09-23: SIXTEEN suites pin `DEMO_FALLBACK_ENABLED: false`, not the ten listed above.
 * The six this list never named are `sections/__tests__/sections1-3.test.tsx`,
 * `sections/__tests__/sections4-5.test.tsx`, `sections/__tests__/sections7-9.test.tsx`,
 * `sections/__tests__/growthHandoffGate.test.tsx`,
 * `sections/__tests__/growthHandoffBlockers.test.tsx` and
 * `customer360/sections/__tests__/BehaviourEngagementSection.test.tsx`. Their mock blocks come out
 * at teardown too. Counted with a grep over the tree, not carried from a report.
 *
 * AND THREE TESTS THAT GO RED AT TEARDOWN, all in
 * `components/__tests__/InsightCardRenderer.test.tsx`. They pass TODAY only because
 * `demoFallbackConfidenceGate.ts` restores the pill — that suite pins no flag, so the bypass reaches
 * it — and each asserts a card-confidence pill on a card the Metrics Catalogue forbids scoring. They
 * are listed rather than edited on purpose: three green tests and this warning were judged better
 * than three edited ones. Deleting this layer makes all three fail, which is exactly when nobody
 * will remember why:
 *
 *   `:167` `renders the descriptor-derived copy, metrics, evidence, confidence and explanation`
 *          — asserts `High confidence · 82%` on `customer_population_activity`, whose catalogue
 *            metrics are all `No`. Re-express or drop the confidence assertion; the rest stands.
 *   `:271` `leaves the reason to the card header rather than drawing it into the body`
 *          — reads `confidence-reason` on the same card. `InsightCard.tsx` ties the reason to the
 *            pill, so it goes when the pill goes. The PLACEMENT rule it tests is still real: move it
 *            to a card that may be scored.
 *   `:288` `keeps the unscored badge reachable for a payload whose score is explicitly null`
 *          — asserts `Confidence unavailable` on `customer_lifecycle_holdings`, also all `No`.
 *            DO NOT DELETE THIS ONE. It encodes a genuine distinction worth preserving — an absent
 *            key means unscored, an explicit `null` means scored with no result, and only the second
 *            still owes the reader a badge. That distinction has nothing to do with the card gate;
 *            the test merely chose an ineligible card to express it on. The fix at teardown is to
 *            re-express it on a catalogue-ELIGIBLE card, never to remove the coverage.
 *
 * A fourth test DID encode the defect and was flipped rather than listed, because its own suite pins
 * the flag false so the bypass cannot reach it and it is red today:
 * `sections/__tests__/sections1-3.test.tsx:1139` now asserts Engagement Funnel draws NO pill.
 */
export const DEMO_FALLBACK_CALL_SITES = 15;

/** The layer a card belongs to — the same discriminator `assembleCard` already takes. */
export type DemoFallbackLayer = 'customer_insights' | 'customer_360';

/**
 * A segment set a card body draws, invented.
 *
 * `replaceWhenUnnamed` is the one case where the demo stands in front of a response that ANSWERED:
 * O-HYB-013 really returns 120 merchants and every key is an opaque handle (`mrc_` + 16 hex), so
 * `humaniseRef` names none of them and the panel takes its Insufficient Data branch. The demo
 * replaces the segments only while that is true of EVERY segment — one resolvable merchant on the
 * wire and the live set is drawn untouched.
 */
interface DemoCardDistribution {
  dimension: string;
  segments: DistributionSegment[];
  replaceWhenUnnamed?: true;
}

/**
 * A card's invented CONFIDENCE pill — the badge `InsightCard` draws beside the card title.
 *
 * INVENTED, like everything else here, and added on 2026-09-21 on the user's explicit instruction;
 * see the relaxation note at the head of this file for the rule it overturns. It is NOT a reading
 * and NOT a catalogue value, and it is not computed from anything: `data/cardConfidence.ts` averages
 * the confidences the SERVER published per metric, and a card reaches this type precisely because no
 * eligible metric on it published one.
 *
 * `score` is a PERCENTAGE, 0-100, which is `CardConfidence.score`'s own unit — the badge prints
 * `{{band}} · {{score}}%`, so a 0-1 fraction here would draw `High confidence · 1%`. `band` is stated
 * rather than derived so the pair cannot silently drift apart, and it must be the band the catalogue
 * floors put `score` in (`data/confidence.ts`: High >=80, Medium 60-79, Low 40-59, Insufficient <40).
 *
 * A card that carries one still publishes NO reason: `reasons` is always `[]`. A leading reason is a
 * governed `reason_code` resolved through the Confidence Reason Catalogue, the relaxation did not
 * reach those, and `InsightCard` already draws a badge with no reason line beneath it.
 */
interface DemoCardConfidence {
  score: number;
  band: ConfidenceBand;
  /**
   * A GOVERNED reason code from the Confidence Reason Catalogue
   * (`data/cardConfidence.ts` § `CONFIDENCE_REASON_RULES`), resolved to copy the same way the real
   * aggregator resolves one. Optional: a demo pill without it publishes an empty reason list and
   * draws the badge alone, which is what every Layer 1 entry does and goes on doing.
   *
   * Never invented prose. The reason line is governed copy on a customer-facing card, so a code the
   * catalogue does not carry, or one with no copy authored, publishes nothing at all.
   */
  reasonCode?: string;
}

interface DemoCardFallback {
  /**
   * Tile key → the figure that tile draws, already in the unit its registry `format` states:
   * `percent` 0-100, `points` 0-100, `count` a whole number, `ratio` around 1, `currency` an amount.
   */
  metrics?: Record<string, number>;
  /**
   * Tile key → the WORD that tile draws, for a categorical reading whose registry `format` is a
   * number only because the union has no `text` member: `Contactability Status`, `Lifecycle Stage`,
   * `Latest Event`. It lands on `CardMetric.valueText`, which is where the pills and status chips of
   * `customer360/sections/` read a categorical reading from — a figure in `value` never reaches them.
   *
   * A tile may declare BOTH: `current_tier` prints `3` on its own tile and `Tier 3` in the identity
   * header's chip, and those are one reading rendered in two places rather than two readings.
   */
  texts?: Record<string, string>;
  /** Tile key → the segment set that tile's panel draws. */
  distributions?: Record<string, DemoCardDistribution>;
  /**
   * The card-level confidence pill, for a card that draws none of its own.
   *
   * Not written per card here: `DEMO_CARD_CONFIDENCE` below owns every one of them and
   * `withCardConfidence` attaches them, so the relaxation is one readable list. Declared ONLY on the
   * cards whose eligible metrics published no `confidence_score` on the wire — a card the server
   * does score keeps the server's figure untouched, because `withDemoFallback` applies this only
   * where the assembled fixture carries no confidence at all.
   */
  confidence?: DemoCardConfidence;
  /**
   * Replace a PROVIDER ERROR (502/504) with the filled card rather than the red error block. Set on
   * the two Product Readiness cards only, whose whole catalogue family 502s on UAT.
   *
   * SUBSUMED on 2026-09-21 and kept as a marker only: `RESCUED_STATES` below now draws over every
   * failure state on every card, so this flag no longer gates anything. It stays because narrowing
   * the rescue back to these two cards is then a one-line change with the mark already in place.
   */
  overridesProviderError?: true;
  /**
   * Product Readiness (Layer 1) only: the cross-tab cells its four tiles AND its two rings are both
   * summed from, so the tiles and the rings cannot state different populations.
   */
  readinessRows?: ProductReadinessRow[];
}

/* The population every invented count below is consistent with, taken from what UAT `generic` really
   answers: 2,500 registered (O-COM-001) and 2,120 eligible (O-COM-001's supporting column). A count
   here is a slice of that base and a rate is that slice over its own denominator, so no tile reports
   more customers than the card beside it. */
const ELIGIBLE = 2120;

/** One `-PR-` cross-tab cell. Only the columns the aggregation reads carry a value. */
const readinessCell = (
  holding: 'holder' | 'non_holder',
  band: string,
  decision: 'eligible' | 'ineligible',
  customers: number
): ProductReadinessRow => ({
  tenant_id: null,
  customer_id: null,
  product_offer_id: null,
  product_code: null,
  product_family: null,
  industry: null,
  catalog_lifecycle_status: null,
  readiness_model_status: null,
  readiness_not_configured: null,
  has_active_holding: null,
  eligibility_decision: decision,
  eligibility_reason_codes: null,
  eligibility_evaluated_at: null,
  propensity_score: null,
  propensity_band: band,
  composite_confidence: null,
  handoff_confidence_ok: null,
  propensity_scored_at: null,
  exclusion_reason: null,
  excluded_from_marketing: null,
  period_start: null,
  period_end: null,
  formula_display_text: null,
  governed_formula_expression: null,
  governed_formula_version: null,
  n_customers: customers,
  target_product_holding_status: holding
});

/* Non-holders 1,640 of which 1,180 eligible; holders 480 of which 395 — 2,120 evaluated and 1,575
   eligible overall, which is the ELIGIBLE base above split two ways. Every cell carries a band, so
   the rings divide exactly the population the tiles count. */
const DEMO_READINESS_CELLS: [
  'holder' | 'non_holder',
  string,
  /* eligible */ number,
  /* ineligible */ number
][] = [
  ['non_holder', 'high', 198, 16],
  ['non_holder', 'medium', 352, 53],
  ['non_holder', 'low', 389, 97],
  ['non_holder', 'insufficient', 241, 294],
  ['holder', 'high', 92, 4],
  ['holder', 'medium', 131, 17],
  ['holder', 'low', 98, 23],
  ['holder', 'insufficient', 74, 41]
];

const DEMO_READINESS_ROWS: ProductReadinessRow[] = DEMO_READINESS_CELLS.reduce(
  (rows, [holding, band, eligible, ineligible]) => [
    ...rows,
    readinessCell(holding, band, 'eligible', eligible),
    readinessCell(holding, band, 'ineligible', ineligible)
  ],
  [] as ProductReadinessRow[]
);

/**
 * Every invented tile figure, by layer and card key.
 *
 * A tile absent from this table is left empty, which stays the default: nothing here is fetched,
 * cached or compared against the wire — `withDemoFallback` reads it ONLY where the assembled tile
 * has no reading of its own, so a live reading always wins.
 *
 * "NEVER PAINT OVER A REFUSAL" WAS REMOVED ON 2026-09-21, on the user's explicit instruction, and
 * this paragraph is what stands in its place. The rule said a tile could be filled only where its
 * catalogue metric was absent, answered with no rows, was not deployed or failed in the provider —
 * and never where the metric answered `insufficient_data` with a governed `reason_code`, because
 * that is the platform DECLINING to score for a stated reason. The demo tenant `digibank` does hold
 * customer records (ids 1-4) and most metrics answer 200 with real values, so the screens were not
 * empty for want of data: they were 75 blank tiles and panels across 13 cards, several of them
 * exactly those governed refusals (`C-COM-003` and `C-COM-005` answer `insufficient_data`,
 * `C-BFS-PR-001` answers 502). The user asked for those gaps filled, which makes an
 * `insufficient_data` tile fillable. `RESCUED_STATES` below therefore draws the filled card over a
 * failure state that means the reading is MISSING.
 *
 * It does NOT reach a reading that was WITHHELD. `permissionRestricted` and `consentRestricted` are
 * excluded by type at `RESCUED_STATES` and are never drawn over, flag or no flag — see the note
 * there. The relaxation is about absent data; it is not a licence to contradict a governance
 * decision the platform has already taken.
 *
 * What remains is still a real loss of honesty, and it is why the layer is switch-guarded rather
 * than merged into the render path: `DEMO_FALLBACK_ENABLED = false` puts every refusal back,
 * unchanged and in full.
 *
 * The table also now carries an entry for EVERY card of both registries rather than only the ones
 * whose tiles were blank on `generic`.
 *
 * RULE 1 WAS "NEVER A CONFIDENCE READING, WHATEVER ITS STATUS", AND IT WAS RELAXED THE SAME DAY.
 *
 * The rule as it stood: a tile whose reading is a confidence score or band is governed even where
 * the registry leaves it `unmapped`, so this file invented neither. Nine tiles were named-and-
 * skipped at their own cards below for it (`identity_confidence` twice,
 * `risk_distribution_confidence`, `result_confidence`, `merchant_category_confidence_score`,
 * `p2p_p2m_classification_confidence`, and the three removals in rule 2), and a card rescued from a
 * failure state dropped its confidence pill entirely rather than show an invented or a stale one.
 * The rule was deliberate, it was enforced, and it is kept written down here so the next reader can
 * see that what follows is a decision and not an oversight.
 *
 * ON 2026-09-21 THE USER ASKED, EXPLICITLY, FOR CONFIDENCE READINGS ON BOTH SCREENS FOR THE DEMO.
 * So, from that date:
 *
 *   - Every card that drew NO confidence badge — because no eligible metric on it published a
 *     `confidence_score` — carries an invented `confidence` below. 18 of Layer 1's 27 cards and 14
 *     of Customer 360's 23. A card the server DOES score is untouched, and `withDemoFallback` only
 *     ever fills a fixture that carries no confidence, so a live 91% or 92% stays a live 91% or 92%.
 *   - A rescued card now takes the invented pill after its stale one is dropped, instead of drawing
 *     none.
 *   - FOUR of the nine skipped tiles carry a figure again: `risk_distribution_confidence` and
 *     `result_confidence` on Layer 1, `identity_confidence` and
 *     `p2p_p2m_classification_confidence` on Customer 360 (the last two in
 *     `demoFallbackCustomer360.ts`, which owns those cards' tiles). Each is `format: 'percent'` and
 *     so renders 0-100 — the wire's own `confidence_score` is a 0-1 fraction, and a verbatim 0.84
 *     copied onto one of these tiles would draw `0.8%`.
 *
 * Two of the nine are NOT re-added and are neither forbidden nor forgotten: Layer 1's
 * `identity_confidence` (O-COM-005) and `merchant_category_confidence_score` (O-HYB-010). The
 * instruction named four tiles and these were not among them; both are `Metric Itself` in the
 * catalogue, so neither feeds a card score either way.
 *
 * Worth knowing before adding more: the catalogue's own card rule is *"if a card has no applicable
 * Yes metrics, do not calculate a standard Card Confidence"*, and many of the cards scored below
 * have none. The invented pill is therefore not merely un-computed, it sits on cards the catalogue
 * says are not to be scored at all. That is the cost of the relaxation and it is why the switch
 * exists.
 *
 * WHAT THE RELAXATION DID NOT REACH, AND WHAT IS STILL NEVER INVENTED:
 *
 *   1. A `reason_code`, a `calculation_status` or a `Why this result?` sentence. A demo card draws
 *      its badge with no reason line beneath it, which is what `InsightCard` already does when a
 *      reason is absent.
 *   2. NOTHING. All five entries removed on 2026-09-21 were RESTORED later the same day.
 *
 *      This clause used to withhold three of them — `sentiment_score`, `incentive_dependency_score`
 *      and `cash_withdrawal_dependency_score` — under the rule "fill a blank, never paint over a
 *      refusal": each sits on a metric that answers `insufficient_data` with a governed reason, and
 *      each shares its card with a live reading the viewer compares it against. The user overrode
 *      that rule explicitly on 2026-09-21, for the demo, on the same instruction that re-added
 *      `risk_distribution_confidence` and `result_confidence`: no `No Data Yet` and no
 *      `Not Configured` may be visible on either Intelligence screen. The three now carry values in
 *      `demoFallbackCustomer360.ts`, and the note at each card below records what its metric really
 *      answers so the reason the value was once withheld is not lost with the withholding.
 *
 *      What that override did NOT reach is clause 1, which still holds outright: the governed
 *      `reason_code` and `calculation_status` behind each of those three are still never drawn.
 *      `filledMetric` DROPS them along with the failed `dataState` rather than restating them over
 *      a figure they no longer describe.
 */
const DEMO_FALLBACK_TABLE: Record<DemoFallbackLayer, Record<string, DemoCardFallback>> = {
  /* LAYER 1 — all 27 cards of `registry/cards.ts`, in that registry's own order so "is every card
     covered?" is answered by reading down the list rather than by cross-referencing two files.
     Every count below is a slice of the 2,500 registered / 2,120 eligible population above. */
  customer_insights: {
    // 1,704 of 2,500 active is 68.2%; 2,120 of 2,500 eligible is 84.8%, leaving 380 excluded. The
    // eligible-active cohort is 1,520 of those 2,120, which is the 71.7% the last tile prints.
    customer_population_activity: {
      metrics: {
        total_customers: 2500,
        active_customers: 1704,
        activity_rate: 68.2,
        eligible_coverage_rate: 84.8,
        eligible_customers: ELIGIBLE,
        ineligible_customers: 380,
        eligible_active_rate: 71.7
      }
    },
    /* `identity_confidence` (O-COM-005) is FILLED on 2026-09-21 under the user's instruction that
       no tile stay blank for the demo. 98.6 is not free-chosen: the card's own alerts row states
       identity conflicts at 1.4% of the registered base, and this tile is that figure's complement,
       so the gauge and the alert cannot contradict each other. O-COM-005 is `Metric Itself` in the
       catalogue, so it feeds no card score and does not disturb the 88 confidence chip.
       UAT serves this tile LIVE at 72.3, so the figure below is reached only on a tenant that does
       not publish O-COM-005 — a live reading always wins over this table.
       `reversal_failure_rate` is NOT listed below, and the removal is deliberate: UAT serves that
       tile LIVE at 24.9%, so the 3.4 that used to sit here could never be reached — and would have
       sprung back, four times too low, the day the wire stopped answering. The Rewards card does not
       draw this metric at all (its rendered KPI row has no Reversal Failure / Retry Rate tile), so
       nothing else needs it either. Removed 2026-09-22 after reading both cards. */
    data_trust_integration_health: {
      metrics: {
        data_freshness_score: 92,
        identity_confidence: 98.6,
        source_coverage_score: 87,
        integration_health_status: 94
      }
    },
    /* The holding bands are the `average_active_holdings` tile's own working: 58.7 / 22.0 / 12.0 /
       7.3 of 2,120 averages to 1.7 holdings, which is what that tile prints, and everything above
       one holding is the 41.3% `multi_product_customers` beside it. */
    customer_lifecycle_holdings: {
      metrics: {
        total_eligible_customers: ELIGIBLE,
        average_active_holdings: 1.7,
        multi_product_customers: 41.3
      },
      distributions: {
        lifecycle_stage_distribution: {
          dimension: 'lifecycle_stage',
          segments: [
            { key: 'new', value: 12.4, count: 263 },
            { key: 'active', value: 38.6, count: 818 },
            { key: 'engaged', value: 24.1, count: 511 },
            { key: 'at_risk', value: 14.2, count: 301 },
            { key: 'dormant', value: 10.7, count: 227 }
          ]
        },
        holding_band_distribution: {
          dimension: 'holding_count_band',
          segments: [
            { key: '1', value: 58.7, count: 1244 },
            { key: '2', value: 22.0, count: 466 },
            { key: '3', value: 12.0, count: 255 },
            { key: '4_plus', value: 7.3, count: 155 }
          ]
        }
      }
    },
    // 2,081 of 2,120 eligible is the 98.1% engagement rate O-COM-011 really answers.
    customer_engagement: {
      metrics: {
        eligible_customers: ELIGIBLE,
        engaged_customers: 2081,
        engagement_rate: 98.1,
        multi_source_coverage: 62.4
      },
      // Sources, not customers: the shares are of the 2,081 engaged and a customer reached through
      // two of them is counted under both, which is what the 62.4% multi-source tile measures.
      distributions: {
        engagement_source_distribution: {
          dimension: 'engagement_source',
          segments: [
            { key: 'app', value: 34.8 },
            { key: 'campaign', value: 26.2 },
            { key: 'transaction', value: 19.4 },
            { key: 'notification', value: 12.7 },
            { key: 'support', value: 6.9 }
          ]
        }
      }
    },
    customer_behaviour_trends: {
      metrics: {
        behavioural_momentum: 64,
        behavioural_deviation: 28,
        disengagement_early_warning: 31,
        habit_strength: 58
      }
    },
    // The six stage counts fall monotonically from the 2,120 eligible who enter, and the final stage
    // IS `campaign_action_completed_customers` — 438 of 2,120, which is the 20.7% completion rate.
    // The two outcome stages follow at the Figma funnel's own ratios (70% issued, 60% of those
    // redeemed), and the issuance rate is the issued 307 of the 2,120 who enter.
    engagement_funnel: {
      metrics: {
        funnel_completion_rate: 20.7,
        customers_entering: ELIGIBLE,
        final_stage_customers: 438,
        notification_opened_customers: 1412,
        notification_clicked_customers: 894,
        campaign_enrolled_customers: 612,
        notification_delivered_customers: 1985,
        campaign_action_completed_customers: 438,
        outcome_issued_customers: 307,
        outcome_redeemed_customers: 184,
        outcome_issuance_rate: 14.5
      }
    },
    // Net margin PER eligible customer, which is the grain the card's own subtitle states.
    current_predicted_customer_value: {
      metrics: { eligible_customers: ELIGIBLE, current_clv: 248.6, predicted_clv: 312.4 }
    },
    // 370 of 2,120 is the 17.4 expectation-gap score O-COM-021 really answers.
    customer_sentiment_expectations: {
      metrics: {
        sentiment_score: 68.4,
        expectation_gap_score: 17.4,
        expectation_gap: 370,
        reversal_engagement_impact: 12.4
      },
      // The four classes O-COM-020 declares, keyed as `sections/ExperienceValueSection.tsx` keys
      // them. Positive-leaning, which is the 68.4 the `sentiment_score` tile above prints.
      distributions: {
        sentiment_class_distribution: {
          dimension: 'sentiment_class',
          segments: [
            { key: 'positive', value: 42.6 },
            { key: 'neutral', value: 27.8 },
            { key: 'mixed', value: 12.4 },
            { key: 'negative', value: 17.2 }
          ]
        }
      }
    },
    /* REVERSALS, one story, REBASED ON THE LIVE TOTAL 2026-09-22. It used to read 4,013 events, of
       which 3,877 reversed an outcome and 136 failed or were retrying. UAT serves this card's
       `Total Reversal Event Count` tile LIVE at 2,996, so the demo split was rendering beside — and
       contradicting — a live count on the same card. The SHAPE is unchanged and was never falsified
       (96.6 / 2.4 / 1.0); only its base was wrong, so the same proportions are struck against 2,996:
       2,894 accepted, 72 failed-and-retrying, 30 still retrying.

       These figures do NOT reproduce the Data Trust card's 24.9%, and must not be made to. That is a
       failure/retry rate over ATTEMPTS; this is a split of EVENTS. Forcing the two to agree would
       invent a relationship neither metric states. `reversal_failure_rate` is gone from this entry
       for the same reason it is gone from the Data Trust one above: the card draws no such tile. */
    reward_loyalty_intelligence: {
      metrics: {
        reward_sensitivity_score: 1.24,
        incentive_dependency_score: 42.6,
        reward_redemption_rate: 61.1,
        points_earn_burn_ratio: 1.18,
        reward_fulfilment_delay_rate: 4.8,
        total_reversal_event_count: 2996,
        reversal_rate: 6.2,
        reversed_outcome: 2894,
        reversal_processing_sla: 36
      },
      /* All four apportion a population the card's own tiles already state, and the first two are
         the same two splits `demoFallbackCustomerInsights.ts` gives this card's fallback columns, so
         a panel drawing the segments and one drawing the constant cannot disagree. Reconciliation's
         three counts sum to the `total_reversal_event_count` tile above them, and the tiers are the
         same 964 / 631 / 374 / 151 of 2,120 that file's `tierRows` list. */
      distributions: {
        reversal_entitlement_distribution: {
          dimension: 'entitlement_type',
          segments: [
            { key: 'points', value: 46.2 },
            { key: 'vouchers', value: 28.4 },
            { key: 'cashback', value: 16.1 },
            { key: 'stamps', value: 9.3 }
          ]
        },
        /* PLA-8828 FE-C13: `transaction_refunded` (38.7) and `provider_cancellation` (24.5) were
           removed — both map to Product-REJECTED reversal reasons, and a demo must not state a
           reason the governance rejected. The remaining three are rescaled from 17.2/11.4/8.2 so the
           distribution still sums to 100; deleting the two outright left a 63.2 hole and the ring
           drew two-thirds empty. Do not reintroduce a reason here without a governed code. */
        reversal_reason_distribution: {
          dimension: 'reversal_reason',
          segments: [
            { key: 'duplicate_issuance', value: 46.7 },
            { key: 'fraud_review', value: 31.0 },
            { key: 'manual_correction', value: 22.3 }
          ]
        },
        reversal_reconciliation_distribution: {
          dimension: 'reconciliation_status',
          segments: [
            { key: 'accepted', value: 96.6, count: 2894 },
            { key: 'failed_retrying', value: 2.4, count: 72 },
            { key: 'retrying', value: 1.0, count: 30 }
          ]
        },
        tier_membership_distribution: {
          dimension: 'tier',
          segments: [
            { key: 'bronze', value: 45.5, count: 964 },
            { key: 'silver', value: 29.8, count: 631 },
            { key: 'gold', value: 17.6, count: 374 },
            { key: 'platinum', value: 7.1, count: 151 }
          ]
        }
      }
    },
    /* `risk_distribution_confidence` carried 82, was removed on 2026-09-21 for inventing a
       confidence reading, and is BACK at 82 the same day under the relaxation at the head of this
       file — the user asked for it by name. The history is kept because the removal was deliberate:
       the tile is `Risk-distribution Confidence`, it is `unmapped` so no response can ever reach it,
       and an invented 82% sits beside this card's live `fraud_anomaly_score` (O-COM-022, 30.617289).
       `format: 'percent'`, so the tile renders 0-100 and 82 means 82% — not the 0-1 fraction the
       wire's own `confidence_score` column carries.

       `fraud_anomaly_score` below is that same 30.617289 rounded, so the demo and the live reading
       agree wherever O-COM-022 does answer. 148 high-risk customers is 7.0% of the 2,120 eligible. */
    risk_unusual_activity: {
      metrics: {
        risk_distribution_confidence: 82,
        fraud_anomaly_score: 30.6,
        high_risk_customers: 148,
        suspicious_reversal_pattern_score: 18.6
      },
      // `high` + `critical` is 7.0% of the 2,120 eligible, which IS the 148 `high_risk_customers`
      // tile above — the ring and the tile divide one population.
      distributions: {
        risk_segment_distribution: {
          dimension: 'risk_band',
          segments: [
            { key: 'low', value: 78.4, count: 1662 },
            { key: 'medium', value: 14.6, count: 310 },
            { key: 'high', value: 5.8, count: 123 },
            { key: 'critical', value: 1.2, count: 25 }
          ]
        }
      }
    },
    // The ratio is `DEMO_BANKING_SPEND`'s own two halves (1,252,400 / 1,251,000), which divide to
    // the 1.00 the panel below it draws.
    debt_spending_health: {
      metrics: {
        essential_discretionary_ratio: 1.0,
        debt_obligation_signal_rate: 34.8,
        arrears_signal_rate: 6.1
      }
    },
    /* A SAFETY NET that will almost never fire. O-BFSI-001..003 answer 200 on UAT `generic` — the
       income coverage tile reads 55.1% live today — and the 504s recorded for them in the capture
       sweep were a transient artefact of bulk load, not a provider failure mode. A live reading
       always wins, so these stand in only if the read happens to fail during the demo.

       68% is `DEMO_BANKING_INCOME_COVERAGE` (1,210 of 1,780 banked) and 14.6% is the 30-day row of
       `DEMO_BANKING_FORECAST_SHORTFALL`, so the tiles and the panels below them cannot disagree. */
    income_cashflow_health: {
      metrics: {
        income_detection_coverage: 68,
        income_stability_score: 76,
        cashflow_health_score: 64,
        cashflow_forecast_risk_rate: 14.6
      }
    },
    // TELCO. Each count is its own rate over the 2,120 eligible: 18.2% is 386, 11.4% is 242.
    churn_service_experience: {
      metrics: {
        service_experience_risk_rate: 18.2,
        subscribers_with_service_risk: 386,
        telco_churn_risk_rate: 11.4,
        high_risk_active_subscribers: 242
      }
    },
    // 2,120 × 18.40 is the 39,008 billable revenue, so the ARPU and the total state one population.
    plan_usage_revenue: {
      metrics: {
        current_arpu: 18.4,
        total_billable_revenue: 39008,
        data_utilisation_rate: 72.6,
        voice_utilisation_rate: 48.3
      }
    },
    // 23.5% of 2,120 is the 498 the Growth Planner eligible tile counts.
    recharge_upgrade_readiness: {
      metrics: {
        recharge_regularity_score: 71,
        regular_recharge_subscribers: 1462,
        plan_upgrade_readiness_rate: 23.5,
        upgrade_growth_planner_eligible: 498
      }
    },
    // INSURANCE. 88.7% of 2,120 is the 1,880 active policies.
    policy_health_claims: {
      metrics: {
        policy_active_rate: 88.7,
        active_policies: 1880,
        premium_payment_health: 81,
        claims_frequency: 0.34
      }
    },
    // 21.7% of 2,120 is the 460 the eligibility tile counts.
    renewal_growth_opportunities: {
      metrics: {
        renewal_risk_rate: 16.8,
        coverage_gap_signal_rate: 29.4,
        rider_cross_sell_readiness: 21.7,
        growth_planner_eligible: 460
      }
    },
    // WALLET. 64.8% of 2,120 is the 1,374 active/eligible users.
    wallet_activity_funding: {
      metrics: {
        wallet_active_user_rate: 64.8,
        active_eligible_users: 1374,
        topup_frequency_score: 58,
        highest_feature_adoption: 42.9
      }
    },
    // 3.7% of 2,120 is the 78 anomalous users; the diversity cohort is the 1,640 customers with
    // classifiable spend, the same cohort `DEMO_BANKING_SPEND` is taken over.
    payment_usage_risk: {
      metrics: {
        payment_diversity_index: 0.72,
        diversity_cohort: 1640,
        wallet_velocity_anomaly_rate: 3.7,
        anomalous_wallet_users: 78
      }
    },
    /* `merchant_category_confidence_score` is still EMPTY, and as with `identity_confidence` above
       that is scope and not prohibition after 2026-09-21: the relaxation named four confidence tiles
       and this was not one. O-HYB-010 is `Metric Itself`, so it feeds no card score.
       The unclassified rate is the complement of the classification
       coverage (100 − 71.5), so the two tiles cannot state different populations. */
    merchant_category_data_quality: {
      metrics: {
        merchant_identification_rate: 76.4,
        category_classification_coverage: 71.5,
        low_confidence_transaction_rate: 12.8,
        unclassified_transaction_rate: 28.5,
        merchant_alias_match_rate: 74.2,
        gateway_masking_resolution_rate: 81.6
      }
    },
    /* MERCHANT AFFINITY. Four invented trading names plus the frame's own trailing
       `Unknown / low confidence` bucket, whose key is deliberately an opaque handle so
       `merchantPanelFor` folds it into that bar rather than naming it. The shares are the Figma
       frame's (`589-28708`..`28730`) and are the top five of a 120-merchant tail, so they are not
       meant to sum to 100. `top_merchant_affinity` above is the leader's own 18.4. */
    merchant_category_preferences: {
      metrics: {
        top_merchant_affinity: 18.4,
        top_spend_category: 27.6,
        avg_spend_per_active_user: 268.9
      },
      distributions: {
        merchant_affinity_distribution: {
          dimension: 'merchant',
          replaceWhenUnnamed: true,
          segments: [
            { key: 'fairprice', value: 18.4 },
            { key: 'grab', value: 15.2 },
            { key: 'amazon', value: 11.8 },
            { key: 'shell', value: 9.1 },
            { key: 'mrc_0000000000000000', value: 4.7 }
          ]
        },
        // The reward-category split, whose leader is the 27.6% `top_spend_category` tile above.
        reward_category_affinity_distribution: {
          dimension: 'reward_category',
          segments: [
            { key: 'dining', value: 27.6 },
            { key: 'groceries', value: 23.1 },
            { key: 'fuel', value: 18.4 },
            { key: 'travel', value: 16.2 },
            { key: 'retail', value: 14.7 }
          ]
        }
      }
    },
    /* `cash_withdrawal_dependency_score` here is O-HYB-006 at TENANT grain and is a different tile
       from the Customer 360 `cash_withdrawal_dependency_score` (C-HYB-005) that rule 2 keeps out —
       that removal was about C-HYB-005 declining to score one customer, which says nothing about
       the tenant-wide reading. */
    payment_behaviour_cash_usage: {
      metrics: {
        qr_payment_ratio: 38.6,
        cash_withdrawal_dependency_score: 24.5,
        transfer_vs_spend_ratio: 0.84
      },
      /* Three views of one period's payments. The rail mix repeats the card's own 38.6% QR tile, and
         the intent mix's `transfer` over its `purchase` is 26.4 / 31.4 = the 0.84
         `transfer_vs_spend_ratio` tile — so no panel restates a tile as a different number. */
      distributions: {
        payment_intent_distribution: {
          dimension: 'payment_intent',
          segments: [
            { key: 'purchase', value: 31.4 },
            { key: 'transfer', value: 26.4 },
            { key: 'bill_payment', value: 18.2 },
            { key: 'withdrawal', value: 14.1 },
            { key: 'top_up', value: 9.9 }
          ]
        },
        p2p_p2m_mix_distribution: {
          dimension: 'p2p_p2m',
          segments: [
            { key: 'p2m', value: 64.3 },
            { key: 'p2p', value: 35.7 }
          ]
        },
        payment_rail_distribution: {
          dimension: 'payment_rail',
          segments: [
            { key: 'card', value: 31.2 },
            { key: 'qr', value: 38.6 },
            { key: 'bank_transfer', value: 16.4 },
            { key: 'wallet', value: 9.3 },
            { key: 'cash', value: 4.5 }
          ]
        }
      }
    },
    product_readiness: { overridesProviderError: true, readinessRows: DEMO_READINESS_ROWS },
    // 1,842 signals across 1,120 customers, and the same 1,842 splits into the handoff card's
    // 1,220 eligible and 622 blocked below.
    /* THE ONE CARD WITH A LIVE OVERRIDE -- see `DEMO_LIVE_OVERRIDES`. Both tiles answer a CALCULATED
       0 on `digibank`, so neither is a blank this file could fill.

       18,420 is the design's own `opportunity_signal_count` off `589:30564`, and the four
       `productInterestCells` beneath it are that same frame's four cells, so the tile and the grid
       state one reading.

       1,120 IS A DELIBERATE DEVIATION FROM THE DESIGN, which prints 31,760 here. That figure is
       impossible twice over: a customer carries one or more signals, so a customer count cannot
       exceed the 18,420 signal count beside it, and 31,760 customers is 12.7x the 2,500 registered
       users the LIVE tile at the top of this same screen states. 1,120 of the 2,120 eligible is the
       same base every other count in this file is a slice of. PLA-8828. */
    growth_opportunities_product_interest: {
      metrics: { opportunity_signal_count: 18420, customers_with_active_signals: 1120 }
    },
    // 322 of 2,120 is the 15.2% suppression rate O-COM-027 really answers.
    contact_restrictions: {
      metrics: { suppression_rate: 15.2, suppressed_customers: 322, eligible_customers: ELIGIBLE }
    },
    /* `result_confidence` carried 84, was removed on 2026-09-21 alongside
       `risk_distribution_confidence` above, and is BACK at 84 the same day for the same reason —
       the user named this tile too. `Result Confidence` is `unmapped` and `format: 'percent'`, so it
       renders 84%; the card's other two tiles stand on a live O-COM-026 (33.578995), so the invented
       84% is drawn beside a real 33.58 and the two say different things about the same card. That
       was the objection to it, and it is recorded rather than removed.

       `nudge_readiness_score` below is that same 33.578995 rounded. */
    ready_for_engagement: {
      metrics: { result_confidence: 84, nudge_readiness_score: 33.6, ready_customers: 712 }
    },
    // 388 + 234 = the 622 blocked, and 1,220 + 622 = the 1,842 signals the Opportunities card counts.
    growth_planner_handoff_readiness: {
      metrics: {
        handoff_eligible_signals: 1220,
        handoff_blocked_signals: 622,
        handoff_blocked_low_confidence: 388,
        handoff_blocked_missing_consent: 234
      }
    }
  },
  /* CUSTOMER 360 — all 23 cards of `registry/customer360Cards.ts`, in that registry's own order.
     ONE customer's grain throughout, and every figure agrees with the `View More` row sets at the
     foot of this file: the tiles and the overlays draw the same events, amounts and counts. */
  customer_360: {
    customer_details: { metrics: { customer_profile_attributes: 12, permissions_availability: 4 } },
    // Trailing and forward 12-month value for this one customer, not the tenant's per-customer
    // margin the Layer 1 CLV card states — a different quantity at a different grain.
    holdings_customer_value: {
      metrics: {
        current_clv_band: 2480,
        predicted_clv_band: 3120,
        product_holding_count_distribution: 4
      }
    },
    /* `identity_confidence` was SKIPPED here and is re-added on 2026-09-21 under the relaxation at
       the head of this file — the user named this tile. It lands in `demoFallbackCustomer360.ts`
       and not here, because that file's entry REPLACES this card's `metrics` wholesale (see
       `DEMO_FALLBACK_VALUES`), so a figure written here would never be read. `current_tier` carries
       both a figure and a word: the tile prints `3`, and `CustomerProfileSection` reads `valueText`
       for the header chip, so without the word the chip stays blank beside a populated tile. */
    profile_summary: {
      metrics: { current_tier: 3 },
      texts: { lifecycle_stage: 'Active', current_tier: 'Tier 3' }
    },
    customer_behaviour_profile: {
      metrics: {
        behavioural_momentum_score: 58.2,
        behavioural_deviation_score: 24.7,
        disengagement_early_warning_score: 33.1,
        habit_strength_score: 61.4
      }
    },
    // C-COM-008 really answers `2026-06-29`, which is 84 days before this file was written — so the
    // date the tile prints and the 84 days beside it are one reading stated twice.
    engagement_timeline: {
      metrics: { engagement_frequency: 14, days_since_last_engagement: 84 },
      texts: { funnel_stage: 'Campaign Enrolled', last_engagement_date: '29 Jun 2026' }
    },
    /* `expectation_gap_score` is the live C-COM-016 reading (9.230769) rounded, so the demo agrees
       with the wire where it answers. `sentiment_score` is supplied by
       `demoFallbackCustomer360.ts`, which replaces this card's `metrics` outright — see below. */
    sentiment_expectations: { metrics: { expectation_gap_score: 9.2 } },
    /* `sentiment_score` carried 62.5, was removed on 2026-09-21, and was RESTORED to 62.5 the same
       day on the user's explicit instruction for the demo (header clause 2). It is set in
       `demoFallbackCustomer360.ts`, the authority for this screen's tiles, not here.

       What it was withheld FOR, kept because it is still true: C-COM-015 answers
       `insufficient_data` / `below_min_sample_d06` on UAT `generic` — the platform declining to
       score this customer because it holds fewer than the governed 3 permitted signals — and the
       card's only other tile, `expectation_gap_score`, is a LIVE C-COM-016 reading (9.230769) the
       reader compares it against directly. 62.5 is a mid-band reading that sits plausibly beside a
       9.2 gap, which is why the restored figure is the original one and not a higher invention.
       The `below_min_sample_d06` reason code itself is still never drawn: `filledMetric` drops it
       with the failed `dataState`. */
    // 11 redeemed of 18 issued is the 61.1% redemption rate C-COM-011 really answers, and the 2
    // reversals C-REV-001 really answers are 11.1% of the same 18.
    //
    /* `incentive_dependency_score` carried 21.8, was removed on 2026-09-21 under the same rule, and
       was RESTORED to 21.8 the same day on the user's explicit instruction for the demo (header
       clause 2). It is set in `demoFallbackCustomer360.ts`, not here.

       What it was withheld FOR, kept because it is still true: C-COM-014 answers
       `insufficient_data` / `below_j03_engagement_or_exposure_minima`, and three tiles on this card
       are live (`reward_redemption_rate` 61.111111, `points_balance_movement` 450, `reversal_count`
       2). 21.8 is a low dependency, which is the same reading the 61.1% redemption rate beside it
       gives — a customer who redeems without needing to be paid to. The reason code is still never
       drawn: `filledMetric` drops it with the failed `dataState`. */
    rewards_loyalty_activity: {
      metrics: {
        reward_sensitivity_score: 58.4,
        reward_redemption_rate: 61.1,
        points_balance_movement: 1.24,
        rewards_issued: 18,
        rewards_redeemed: 11,
        rewards_expired: 3,
        reversal_count: 2,
        upcoming_expiry_30d: 2,
        reversed_entitlement_summary: 1250,
        reversal_reason_profile: 11.1,
        reversal_timeline_events: 2,
        tier_progression: 64
      }
    },
    /* `Evidence Pack Completeness` (C-COM-022) is a COVERAGE reading — how much of the evidence pack
       is present — and not a confidence score or band, so rule 1 does not reach it. It is this
       card's only tile, so skipping it would leave the one card the rescue cannot help. */
    data_confidence_supporting_evidence: { metrics: { evidence_pack_completeness: 82.5 } },
    // `affected_events_30d` IS the length of `DEMO_C360_AFFECTED_EVENTS` below: the tile counts the
    // rows the timeline beside it lists, and the timeline needs more than `C360_INLINE_ROW_LIMIT`
    // of them before its `View More` is drawn at all. `latest_event` and `highest_severity_30d` are
    // that same row set read two other ways — its first row's date, and the worst severity in it.
    risk_unusual_activity: {
      metrics: { fraud_anomaly_score: 34.2, affected_events_30d: 6, impacted_metrics_decisions: 4 },
      texts: { latest_event: '12 May 2026', highest_severity_30d: 'High' }
    },
    /* Monthly outflow is 4,820 − 1,180 = 3,640, which `DEMO_C360_SPEND_BREAKDOWN` splits
       45 / 30 / 15 / 10 into 1,638 / 1,092 / 546 / 364. The 862 of EMIs in `DEMO_C360_DEBT_ROWS`
       sits across two of those: the 430 home-loan instalment under Essentials and the other four
       (432) under the 546 of Financial — so the debt tile, the donut and the 17.9%-of-income line
       in `DEMO_C360_HEALTH_INSIGHTS` all describe the same month. */
    financial_health: {
      metrics: {
        income_monthly: 4820,
        net_cashflow_trend: 1180,
        debt_obligation_ratio: 862,
        account_balance: 7640,
        financial_stress_signal: 38.4,
        income_stability_score: 78.5,
        essential_discretionary_split: 45
      },
      texts: { cashflow_strength: 'Stable' }
    },
    /* `cash_withdrawal_dependency_score` carried 28.6, was removed on 2026-09-21, and was RESTORED
       to 28.6 the same day on the user's explicit instruction for the demo (header clause 2). It is
       set in `demoFallbackCustomer360.ts`, not here.

       What it was withheld FOR, kept because it is still true: C-HYB-005 answers
       `insufficient_data` / `below_d11_min_cash_withdrawals_5` — fewer than the governed five
       withdrawals to score on. The demo's own `DEMO_C360_PANEL_ROWS.cashUsage.recentWithdrawals`
       carries FOUR rows, so the card states in its own panel exactly the shortfall the refusal names
       — which is why 28.6 is drawn as a dependency score and the `5` minimum is never restated as a
       reason line. 28.6 sits just under the 31.2 `cash_dependency_score` beside it, the two being
       the same customer read narrowly and broadly, and both agree with the 1,450 withdrawn against
       the month's 3,640 outflow. The reason code is still never drawn: `filledMetric` drops it with
       the failed `dataState`.

       The four below are all `unmapped` in `registry/customer360MetricIdentifiers.ts`: no catalogue
       metric reaches them at all. */
    cash_usage_dependency: {
      metrics: {
        cash_dependency_score: 31.2,
        cash_withdrawals: 1450,
        post_income_withdrawals: 890,
        // Same rule as `affected_events_30d`: the tile counts the rows of
        // `DEMO_C360_IMPACT_ON_INSIGHTS`, which needs a fourth before its `View More` exists.
        impact_on_insights: 5
      }
    },
    /* `p2p_p2m_classification_confidence` was SKIPPED here and is re-added on 2026-09-21 under the
       relaxation at the head of this file — the user named this tile. Like `identity_confidence`
       above it lands in `demoFallbackCustomer360.ts`, which replaces this card's `metrics`.
       `spend_vs_active_user_average` is the tenant's own
       268.90 (the Layer 1 `avg_spend_per_active_user` tile), because the card prints this
       customer's 412.35 against it and two different numbers for one quantity would be a bug the
       reader can see. `transfer_vs_spend_ratio` is a PERCENT here where Layer 1's is a ratio:
       0.84 of spend as transfers is a 45.7% share, so the two state the same behaviour. */
    payment_merchant_behaviour: {
      metrics: {
        top_merchant_affinity: 22.4,
        category_affinity_reward_preference: 31.8,
        average_spend: 412.35,
        qr_payment_ratio: 41.2,
        spend_vs_active_user_average: 268.9,
        transfer_vs_spend_ratio: 45.7,
        merchant_alias_resolution_profile: 88.6
      }
    },
    // 28.5% unclassified is what 71.5% coverage leaves, and 28.5% of the 3,407 transactions read is
    // the 971 that `DEMO_C360_UNCLASSIFIED_REASONS` accounts for row by row.
    transaction_data_quality: {
      metrics: {
        merchant_identification_confidence: 79.4,
        category_classification_coverage: 71.5,
        unclassified_transaction_rate: 28.5,
        merchant_alias_match_rate: 71.5,
        gateway_masking_resolution_rate: 84.2
      }
    },
    payment_usage_risk: { metrics: { payment_diversity: 4, wallet_velocity_risk: 22.8 } },
    wallet_activity_adoption: {
      metrics: {
        wallet_activity_score: 67.5,
        wallet_balance_trend: 1240,
        next_feature_propensity: 0.72
      }
    },
    policy_health_claims: {
      metrics: { premium_payment_risk: 18.4, claim_experience_score: 74.2 },
      texts: { policy_status_health: 'Active' }
    },
    renewal_coverage_opportunities: {
      metrics: { renewal_risk: 26.5, coverage_gap_signal: 34.0, rider_readiness_band: 62 }
    },
    churn_service_experience: {
      metrics: { service_experience_risk: 21.6, telco_churn_risk: 17.9 }
    },
    plan_usage_upgrade_readiness: {
      metrics: { plan_utilization: 78.4, recharge_gap_days: 6, upgrade_readiness_band: 58 }
    },
    /* `Restricted`, not `Contactable`: the most recent row of `DEMO_C360_RESTRICTION_HISTORY` is the
       12 May frequency cap, and that panel's own note says the history ends where the card's
       Contactability pill starts. `ready_for_engagement` below states the same fact as a status. */
    contact_restrictions: { texts: { do_not_nudge_state: 'Restricted' } },
    // 3 of the 5 active signals are eligible to act on.
    customer_opportunities_product_interest: {
      metrics: {
        active_opportunity_signals: 5,
        eligible_opportunities: 3,
        product_level_propensity_score: 0.78
      }
    },
    ready_for_engagement: {
      metrics: { nudge_readiness_score: 64.8 },
      texts: { handoff_status: 'On hold' }
    },
    /* Every readiness product the registry declares, so the card fills whatever industry family the
       tenant is granted rather than only the nine banking ones. The three words are the card's
       categorical tiles; `affordability_ratio` is a percent of income, not a score. */
    product_readiness: {
      overridesProviderError: true,
      texts: {
        credit_card_holding: 'Held',
        readiness_status: 'Ready',
        delinquency_status: 'None'
      },
      metrics: {
        affordability_ratio: 38.5,
        credit_card_limit_upgrade_readiness: 72.4,
        personal_loan_readiness: 58.1,
        mortgage_home_loan_readiness: 41.6,
        auto_secured_loan_readiness: 46.9,
        overdraft_credit_line_readiness: 63.2,
        savings_account_deposit_product_readiness: 78.5,
        term_deposit_readiness: 54.7,
        investment_wealth_product_readiness: 49.3,
        insurance_cross_sell_readiness: 66.8,
        wallet_activation_reactivation_readiness: 74.1,
        wallet_top_up_readiness: 69.3,
        bill_payment_adoption_readiness: 61.8,
        p2p_p2m_qr_adoption_readiness: 57.4,
        wallet_linked_card_readiness: 52.6,
        bnpl_credit_readiness: 44.2,
        savings_investment_feature_readiness: 48.7,
        insurance_protection_feature_readiness: 55.9,
        merchant_business_services_readiness: 36.4,
        policy_renewal_readiness: 76.2,
        coverage_top_up_readiness: 58.8,
        rider_add_on_readiness: 62.5,
        life_insurance_cross_sell_readiness: 47.3,
        health_insurance_cross_sell_readiness: 64.1,
        motor_insurance_cross_sell_readiness: 39.8,
        travel_insurance_readiness: 42.7,
        home_property_insurance_readiness: 45.6,
        premium_payment_plan_readiness: 68.4,
        lapse_prevention_service_recovery_readiness: 33.9,
        mobile_plan_upgrade_readiness: 71.5,
        data_pack_add_on_readiness: 66.2,
        roaming_pack_readiness: 34.7,
        device_upgrade_financing_readiness: 53.1,
        family_multi_line_plan_readiness: 48.9,
        broadband_fibre_readiness: 59.6,
        content_ott_bundle_readiness: 63.8,
        retention_renewal_offer_readiness: 70.3
      }
    }
  }
};

/**
 * DEMO CONFIDENCE — the invented card-level pill, per layer and per card key.
 *
 * ADDED 2026-09-21 on the user's explicit instruction; the rule it relaxes, and what the relaxation
 * does not reach, are at the head of this file and at `DEMO_FALLBACK_TABLE`. Kept as its own table
 * rather than a field inside each card's figures above for three reasons: the relaxation is the one
 * thing in this file that overturns a written rule and it should be readable in one place; the list
 * below IS the answer to "which cards drew no badge", checkable by reading it; and deleting the
 * relaxation alone — without unwinding the rest of the demo layer — is then removing this constant
 * and `withCardConfidence`.
 *
 * WHICH CARDS ARE HERE. Exactly the cards on which no eligible metric publishes a `confidence_score`,
 * so `data/cardConfidence.ts` returns `undefined` and `InsightCard` draws no pill at all. Eligibility
 * is the catalogue's `Show Confidence Score? = Yes` (`registry/customerInsightsConfidence.ts`,
 * `registry/customer360Confidence.ts`); the metrics that carry the five confidence columns on the
 * wire are the two lists named in `data/confidence.ts`. Crossing the two left 18 of Layer 1's 27
 * cards and 14 of Customer 360's 23 on 2026-09-21; a browser pass on 2026-09-22 found six more
 * Layer 1 cards pill-less and they are added below, taking that half to 24 of 27. A card the server
 * scores is NOT here and must not be: `withDemoFallback` would ignore an entry for it anyway.
 *
 * THE FIGURES ARE INVENTED AND VARIED ON PURPOSE. Nothing is derived from the tiles on the card: a
 * demo in which every card reads the same figure looks synthetic, and one in which the figure looks
 * computed invites the reader to check the arithmetic. Each `band` is the band `score` falls in by
 * the catalogue's own floors (`data/confidence.ts`), so the word and the number beside it always
 * agree. Roughly: complete, counted readings score high; modelled, predictive and industry readings
 * score lower, which is the shape the catalogue's own component weighting would produce.
 */
const DEMO_CARD_CONFIDENCE: Record<DemoFallbackLayer, Record<string, DemoCardConfidence>> = {
  /* LAYER 1 — the 24 of 27 cards of `registry/cards.ts` that draw no pill of their own. The three
     absent here are Customer Behaviour Trends, Customer Sentiment & Expectations and Risk & Unusual
     Activity, which the 2026-09-22 browser pass did NOT find pill-less: the server scores them. */
  customer_insights: {
    // Counted populations off the customer table — the best-covered readings on the screen.
    customer_population_activity: {
      score: 94,
      band: 'high',
      reasonCode: 'INPUT_COVERAGE_COMPLETE'
    },
    data_trust_integration_health: { score: 88, band: 'high', reasonCode: 'SOURCE_VALIDATED' },
    customer_lifecycle_holdings: { score: 83, band: 'high', reasonCode: 'ENTITY_MATCH_STRONG' },
    // Engagement is stitched across four sources, so it reads a little below the counted cards.
    customer_engagement: { score: 79, band: 'medium', reasonCode: 'INPUT_COVERAGE_PARTIAL' },
    engagement_funnel: { score: 76, band: 'medium', reasonCode: 'INPUT_COVERAGE_PARTIAL' },
    // Forward-looking value: a model output, banded well below the counts it is built on.
    current_predicted_customer_value: {
      score: 68,
      band: 'medium',
      reasonCode: 'SOURCE_RELIABILITY_LOW'
    },
    reward_loyalty_intelligence: { score: 85, band: 'high', reasonCode: 'DATA_FRESH_WITHIN_SLA' },
    debt_spending_health: { score: 62, band: 'medium', reasonCode: 'INPUT_COVERAGE_PARTIAL' },
    churn_service_experience: { score: 71, band: 'medium', reasonCode: 'IDENTITY_MATCH_PARTIAL' },
    plan_usage_revenue: { score: 81, band: 'high', reasonCode: 'SOURCE_VALIDATED' },
    recharge_upgrade_readiness: { score: 64, band: 'medium', reasonCode: 'DATA_PARTIALLY_DELAYED' },
    // The insurance pair reads lowest on Layer 1: the thinnest source coverage of the industry sets.
    policy_health_claims: { score: 57, band: 'low', reasonCode: 'INPUT_COVERAGE_LOW' },
    renewal_growth_opportunities: { score: 52, band: 'low', reasonCode: 'SOURCE_CONFLICT' },
    wallet_activity_funding: { score: 77, band: 'medium', reasonCode: 'ENTITY_MATCH_PARTIAL' },
    payment_usage_risk: { score: 66, band: 'medium', reasonCode: 'IDENTITY_MATCH_PARTIAL' },
    product_readiness: { score: 73, band: 'medium', reasonCode: 'INPUT_COVERAGE_PARTIAL' },
    // Consent and suppression flags are authoritative records rather than derived readings.
    contact_restrictions: { score: 90, band: 'high', reasonCode: 'SOURCE_VALIDATED' },
    growth_planner_handoff_readiness: {
      score: 58,
      band: 'low',
      reasonCode: 'CRITICAL_INPUT_MISSING'
    },
    /* ADDED 2026-09-22. Six more Layer 1 cards, each catalogue-eligible (at least one `yes` metric in
       `registry/customerInsightsConfidence.ts`) yet drawing no pill in the browser: their eligible
       metrics answer without the five confidence columns, so `data/cardConfidence.ts` publishes
       nothing. The block above assumed all nine of the cards it excluded were scored live; a browser
       pass over the demo tenant found these six pill-less. */
    // Three of four eligible readings answer live; O-BFSI-003 returns the governed
    // `industry_out_of_scope` refusal on a non-Banking tenant, which is what holds this to Low.
    income_cashflow_health: { score: 56, band: 'low', reasonCode: 'SOURCE_UNAVAILABLE' },
    merchant_category_data_quality: {
      score: 86,
      band: 'high',
      reasonCode: 'IDENTITY_MATCH_STRONG'
    },
    // One eligible metric only (O-HYB-013); O-HYB-014/015 are `No`, and the two leader tiles are the
    // head of a distribution rather than readings of their own.
    merchant_category_preferences: {
      score: 65,
      band: 'medium',
      reasonCode: 'ENTITY_MATCH_PARTIAL'
    },
    // Held under the governed 0.59 Critical cap: the shape `customerInsightsCardConfidence.test.ts`
    // records for this card is O-HYB-006 at 0.59 with `CRITICAL_SOURCE_UNAVAILABLE`, O-HYB-007 null.
    payment_behaviour_cash_usage: {
      score: 54,
      band: 'low',
      reasonCode: 'CRITICAL_SOURCE_UNAVAILABLE'
    },
    // O-COM-024 scores 0.9274 live but O-COM-025 sits at the 0.59 floor, and the card's own signal
    // count is a calculated zero on UAT — banded nearer the weaker of the two.
    growth_opportunities_product_interest: {
      score: 67,
      band: 'medium',
      reasonCode: 'INPUT_COVERAGE_PARTIAL'
    },
    // The card draws its own `Result Confidence` tile at 84%, so the pill reads the same figure
    // rather than contradicting a number printed beside it.
    ready_for_engagement: { score: 84, band: 'high', reasonCode: 'DATA_NEAR_REAL_TIME' }
  },
  /* CUSTOMER 360 — the 14 of 23 cards of `registry/customer360Cards.ts` that draw no pill. The 9
     absent here are scored live: Holdings & Customer Value, Customer Behaviour Profile, Sentiment &
     Expectations, Financial Health, Cash Usage & Dependency, Payment & Merchant Behaviour,
     Transaction Data Quality, Customer Opportunities & Product Interest and Ready for Engagement.

     MOST of the entries below never reach the screen and are kept only so deleting the demo removes
     one table: `engagement_timeline`, `rewards_loyalty_activity`, `risk_unusual_activity` and
     `contact_restrictions` have no `yes` metric in `registry/customer360Confidence.ts`, so the
     catalogue's own rule — *"if a card has no applicable Yes metrics, do not calculate a standard
     Card Confidence"* — makes `customer360CardShowsConfidence` false and `Customer360Card` passes
     `undefined` whatever this table says.

     `profile_summary` is the ONE exception, on an explicit instruction of 2026-09-24:
     `demoFallbackConfidenceGate.ts` § `withDemoCustomer360CardConfidenceGate` bypasses that card
     gate for this one card so the demo shows a pill there as the Layer 1 cards do. It is named
     there, once, and no other Customer 360 card is widened with it. DEMO ONLY. */
  customer_360: {
    // One customer's own record: attributes and permissions are read, not inferred.
    customer_details: { score: 96, band: 'high', reasonCode: 'INPUT_COVERAGE_COMPLETE' },
    /* The one Customer 360 card the demo bypasses the card gate for, so it carries a reason code
       too: the cluster it draws in renders the badge AND the governed `Reason:` line, and a
       badge with no reason beside the sibling cards' reasons reads as a card missing one.
       `INPUT_COVERAGE_COMPLETE` is a Positive reason, which the sheet allows only when no
       Limiting reason is active — true of an invented high score by construction. */
    profile_summary: { score: 87, band: 'high', reasonCode: 'INPUT_COVERAGE_COMPLETE' },
    engagement_timeline: { score: 82, band: 'high', reasonCode: 'DATA_FRESH_WITHIN_SLA' },
    rewards_loyalty_activity: { score: 89, band: 'high', reasonCode: 'SOURCE_VALIDATED' },
    data_confidence_supporting_evidence: {
      score: 74,
      band: 'medium',
      reasonCode: 'SOURCE_CONFLICT'
    },
    // Anomaly detection on one customer's 30-day window — a short series and a model on top of it.
    risk_unusual_activity: { score: 61, band: 'medium', reasonCode: 'INPUT_COVERAGE_PARTIAL' },
    product_readiness: { score: 69, band: 'medium', reasonCode: 'INPUT_COVERAGE_PARTIAL' },
    payment_usage_risk: { score: 63, band: 'medium', reasonCode: 'IDENTITY_MATCH_PARTIAL' },
    wallet_activity_adoption: { score: 78, band: 'medium', reasonCode: 'DATA_PARTIALLY_DELAYED' },
    policy_health_claims: { score: 55, band: 'low', reasonCode: 'INPUT_COVERAGE_LOW' },
    renewal_coverage_opportunities: { score: 49, band: 'low', reasonCode: 'INPUT_COVERAGE_LOW' },
    churn_service_experience: { score: 72, band: 'medium', reasonCode: 'IDENTITY_MATCH_PARTIAL' },
    plan_usage_upgrade_readiness: { score: 59, band: 'low', reasonCode: 'SOURCE_RELIABILITY_LOW' },
    contact_restrictions: { score: 92, band: 'high', reasonCode: 'SOURCE_VALIDATED' }
  }
};

/**
 * One layer's cards with their invented pill attached, for the cards `DEMO_CARD_CONFIDENCE` names.
 *
 * Applied LAST, after the Customer 360 halves are merged, so it cannot be dropped by that merge's
 * per-card shallow spread — `demoFallbackCustomer360.ts` declares tile figures and never a card
 * confidence, and a card it names would otherwise replace this field along with `metrics`.
 */
const withCardConfidence = (
  layer: DemoFallbackLayer,
  cards: Record<string, DemoCardFallback>
): Record<string, DemoCardFallback> =>
  Object.keys(cards).reduce((scored, key) => {
    const confidence = DEMO_CARD_CONFIDENCE[layer][key];

    return { ...scored, [key]: confidence ? { ...cards[key], confidence } : cards[key] };
  }, {} as Record<string, DemoCardFallback>);

/**
 * Whether this file invents a card-level pill for the card — the question
 * `demoFallbackConfidenceGate.ts` asks before bypassing the catalogue's own card gate.
 *
 * Reads the table and NOT `DEMO_FALLBACK_ENABLED`, deliberately. The flag is read one module out,
 * because the test guards named in `DEMO_FALLBACK_CALL_SITES` switch this layer off by MOCKING
 * this module: a function defined here and reached through `jest.requireActual` would close over
 * the real `true` and go on bypassing in a suite that believes the demo is off.
 */
export const demoScoresCard = (layer: DemoFallbackLayer, cardKey: string): boolean =>
  DEMO_CARD_CONFIDENCE[layer][cardKey] !== undefined;

/**
 * The table `withDemoFallback` actually reads, with the Customer 360 half merged in from the file
 * that owns it.
 *
 * `demoFallbackCustomer360.ts` is the AUTHORITY on Customer 360 tile figures — it is maintained
 * against what UAT really answers for the demo customer, and the `customer_360` block above is the
 * base it lands on, so a card that file has not reached still draws something. The merge is PER CARD
 * KEY and shallow on purpose: a card the split file declares replaces this file's `metrics` and
 * `texts` for that card outright (its `engagement_frequency` is the live 35, this file's was 14),
 * while `readinessRows` and `distributions`, which only this file carries, survive underneath.
 * A top-level spread would instead have dropped whichever card the other file happens not to name.
 *
 * DEMO ONLY, PLA-8828 2026-09-21 — deleting the split file leaves this reading the block above.
 */
export const DEMO_FALLBACK_VALUES: Record<DemoFallbackLayer, Record<string, DemoCardFallback>> = {
  customer_insights: withCardConfidence('customer_insights', DEMO_FALLBACK_TABLE.customer_insights),
  customer_360: withCardConfidence(
    'customer_360',
    Array.from(
      new Set([
        ...Object.keys(DEMO_FALLBACK_TABLE.customer_360),
        ...Object.keys(DEMO_C360_TILE_VALUES)
      ])
    ).reduce(
      (cards, key) => ({
        ...cards,
        [key]: { ...DEMO_FALLBACK_TABLE.customer_360[key], ...DEMO_C360_TILE_VALUES[key] }
      }),
      {} as Record<string, DemoCardFallback>
    )
  )
};

/**
 * The Customer 360 panel row sets, for the section files that draw a block rather than a tile.
 *
 * Re-exported rather than left to be imported separately so the demo keeps ONE entry point and one
 * deletion checklist. `demoFallbackCustomer360Rows.ts` owns the shape and the content, and each
 * section reads its own branch behind that file's `DEMO_FALLBACK_ENABLED` ternary — the switch is
 * still the single thing that turns all of this off.
 */
export { DEMO_C360_PANEL_ROWS };

/**
 * The Layer 1 panel figures for the telco, insurance and fintech bands, re-exported for the same
 * reason as the row sets above: one entry point, one deletion checklist.
 *
 * `demoFallbackIndustry.ts` owns them. They are the three-industry counterpart of the
 * `DEMO_BANKING_*` constants further down this file — `sections/IndustrySection.tsx` feeds its
 * bespoke panel bodies from three module-scope objects, and only the Banking one has ever had demo
 * values. Each pair is filled/empty so the consumer's `DEMO_FALLBACK_ENABLED` ternary restores the
 * exact empty set the section ships today when the switch is off.
 *
 * DEMO ONLY, PLA-8828 2026-09-21.
 */
export {
  DEMO_FINTECH_PANELS,
  DEMO_INSURANCE_PANELS,
  DEMO_TELCO_PANELS,
  NO_FINTECH_PANELS,
  NO_INSURANCE_PANELS,
  NO_TELCO_PANELS
};

/**
 * The Customer 360 per-product readiness layouts' figures, same file and same reason.
 *
 * `customer360/productReadiness/registry.ts` resolves a bespoke component per product and each draws
 * blocks the readiness schema has no column for. `CreditCardNoHoldingLayout` gets none of this on
 * purpose — see the note at `demoFallbackIndustry.ts` for why it never mounts; the credit-card set
 * here is for the card-holding body `ProductReadinessSection` draws itself in its place.
 *
 * DEMO ONLY, PLA-8828 2026-09-21.
 */
export {
  DEMO_CREDIT_CARD_HOLDING_LAYOUT,
  DEMO_MORTGAGE_LAYOUT,
  DEMO_POLICY_RENEWAL_LAYOUT,
  NO_CREDIT_CARD_HOLDING_LAYOUT,
  NO_MORTGAGE_LAYOUT,
  NO_POLICY_RENEWAL_LAYOUT
};

/** A tile has a reading of its own — a figure or a word — and must never be overwritten. */
// A live `-PR-` row is a reading even when it states only a governed state with no score: the demo
// fills a product only where the wire answered with no row for it. PLA-8828.
const hasReading = (metric: CardMetric): boolean =>
  metric.value !== null ||
  metric.valueText !== undefined ||
  (metric.readiness !== undefined && metric.readiness.length > 0);

/**
 * THE ONE PLACE THIS FILE CONTRADICTS A LIVE API READING. DEMO ONLY, added 2026-09-21.
 *
 * DELETE THIS FIRST when the demo layer goes: it is the only part of it that can hide something the
 * platform actually answered, and it is the only rule here that cannot be justified as "a blank the
 * wire left". Everywhere else in this file a live reading wins, unconditionally.
 *
 * WHY IT EXISTS. `O-COM-024` answers `metric_value: 0`, `calculation_status: calculated`,
 * `reason_code: null` and `opportunity_active_customer_count: 0` on the demo tenant `digibank`; its
 * own `formula_display_text` ends "(zero is evidenced)". So the zero is a MEASUREMENT, not an
 * absence, and `hasReading` correctly refuses to fill over it. That left the card stating 0 signals
 * at the top while the grid below apportioned the design's 14,060 -- and emptying the grid instead
 * left a headline 0 on a demo screen. The user was given both costs, including that anyone
 * cross-checking this card against the platform mid-demo sees a mismatch, and chose the filled card.
 *
 * WHY IT IS A LIST AND NOT A FLAG. `hasReading` and `withDemoFallback`'s default rule are untouched,
 * so no other tile on either screen can be masked by accident. Three keys across two cards is the
 * whole blast radius; anything added here is a new, deliberate lie and needs its own reason recorded.
 */
const DEMO_LIVE_OVERRIDES: Record<DemoFallbackLayer, Record<string, readonly string[]>> = {
  customer_insights: {
    growth_opportunities_product_interest: [
      'opportunity_signal_count',
      'customers_with_active_signals'
    ]
  },
  /* DEMO ONLY, PLA-8828 2026-09-22. `C-COM-006` answers 0 for this customer, and a zero is the one
     live reading a score panel cannot draw a curve from: `demoScoreCurve` scales its shape by the
     panel's own reading so the last point IS the printed figure, and every point of a zero-scaled
     curve is zero. `ScoreTrendChart` normalises a flat series to the TOP of its band, so the panel
     would draw a high, level line beside a printed 0. Overridden to the 24.7 this file's sibling
     already states for the tile, which is a mid-band deviation consistent with the 50 momentum and
     45.8 disengagement the wire gives the same customer. */
  customer_360: { customer_behaviour_profile: ['behavioural_deviation_score'] }
};

/**
 * The card states the demo draws over, as a RECORD rather than a list so the compiler checks the
 * set: `Exclude<CardDataStateKind, …>` fails the build the day a tenth state joins the union, which
 * is the whole point — a new state that nobody classified here would either show a no-data block on
 * a demo screen or, worse, be painted over when it should not be.
 *
 * FOUR STATES ARE EXCLUDED, and two of those exclusions are not about the demo at all:
 *
 *   `permissionRestricted` and `consentRestricted` are NEVER rescued, whatever the flag says. They
 *   are not an absence waiting to be filled: one states that the viewer is not entitled to this
 *   reading, the other that the customer withheld consent to it. Both are governance decisions the
 *   platform has already taken, and drawing invented figures over either asserts the opposite of
 *   what the platform decided — on Customer 360 that is per-customer, so it would mean showing data
 *   for someone who refused it. That is a different kind of wrong from the rest of this file and no
 *   demand for a populated screen reaches it. They are excluded by TYPE above, so re-adding one is
 *   a deliberate edit to this line and not a stray key in the object below.
 *
 *   `ok` is absent because there is nothing to rescue (its blank tiles are still filled, which is
 *   what this file did before today), and `loading` because a card that is still fetching has not
 *   failed: figures drawn over a skeleton would flash and then be replaced by the wire's.
 *
 * What is left all means the data is MISSING rather than withheld — broken, uncalculated, not set
 * up, not yet landed — plus `noMatchingCustomers`, which is an empty result and not a refusal.
 */
const RESCUED_STATES: Record<
  Exclude<CardDataStateKind, 'ok' | 'loading' | 'permissionRestricted' | 'consentRestricted'>,
  true
> = {
  technicalError: true,
  insufficientData: true,
  notConfigured: true,
  awaitingData: true,
  noMatchingCustomers: true
};

const isRescuedState = (kind: CardDataStateKind): boolean =>
  Object.prototype.hasOwnProperty.call(RESCUED_STATES, kind);

/**
 * One tile, filled — with a figure, a word, or both.
 *
 * The emptiness markers go with the emptiness: a tile drawing a figure must not also carry the
 * `notConfigured` flag, the failed `dataState` or the governed `calculation_status`/reason code that
 * explained why it had nothing. None of those is FABRICATED here — they are dropped, because they no
 * longer describe what the tile shows.
 */
const filledMetric = (metric: CardMetric, value?: number, text?: string): CardMetric => {
  const { notConfigured, dataState, unavailableStatus, unavailableReasonCode, ...kept } = metric;

  return {
    ...kept,
    ...(typeof value === 'number' ? { value } : {}),
    ...(text === undefined ? {} : { valueText: text })
  };
};

/**
 * The Product Readiness tiles, summed off the invented cross-tab rather than listed one by one, and
 * carrying those cells on so `sections/productReadinessLive.ts` draws both rings from the same
 * numbers the tiles print.
 */
const filledReadinessMetrics = (
  metrics: CardMetric[],
  rows: ProductReadinessRow[]
): CardMetric[] => {
  const totals = aggregateReadiness(rows);

  return metrics.map(metric => {
    if (hasReading(metric)) {
      return metric;
    }

    const value = readinessTileValue(metric.key, totals);

    return typeof value === 'number' ? { ...filledMetric(metric, value), readiness: rows } : metric;
  });
};

/**
 * The card's segment sets, with an invented one put where the panel would otherwise draw nothing.
 *
 * A live set that names even ONE of its segments is returned untouched — see `replaceWhenUnnamed`.
 */
const withDemoDistributions = (
  live: Record<string, CardDistribution>,
  demo: Record<string, DemoCardDistribution>,
  labelFor: (key: string) => string | undefined
): Record<string, CardDistribution> | undefined => {
  // `undefined` and never a copy, so the caller can tell "nothing was substituted" from "one was"
  // by identity rather than by re-deciding the rule.
  let substituted: Record<string, CardDistribution> | undefined;

  Object.keys(demo).forEach(key => {
    const present = live[key];
    const names = (present?.segments ?? []).some(segment => segmentName(segment) !== null);

    if (present && (names || !demo[key].replaceWhenUnnamed)) {
      return;
    }

    const label = labelFor(key) ?? present?.label;

    if (label === undefined) {
      return;
    }

    substituted = {
      ...(substituted ?? live),
      [key]: { key, label, dimension: demo[key].dimension, segments: demo[key].segments }
    };
  });

  return substituted;
};

/**
 * The assembled card, with invented figures put on the tiles that would otherwise render empty.
 *
 * A live reading ALWAYS wins: a tile carrying a value, a `valueText` or a categorical word is
 * returned untouched, and a card whose every tile read is returned as the identical object.
 *
 * THAT RULE COVERS CONFIDENCE TOO, and it is the whole of how the 2026-09-21 relaxation is kept
 * safe: `withDemoConfidence` below attaches `DEMO_CARD_CONFIDENCE`'s invented pill only to a fixture
 * that carries NO confidence field, so a card the server scored keeps the server's score and band
 * untouched. An invented pill can therefore never overwrite, round, re-band or shift a live one.
 *
 * A card rescued out of a failure state still DROPS the wire's `confidence`, `whyThisResult` and
 * `whyThisResultReasons`: they were the wire's account of why the card had nothing to show, the card
 * now shows something, and a governed sentence explaining an absence printed over invented figures
 * would be the one genuinely misleading thing this file could do. What changed on 2026-09-21 is only
 * what happens next — the drop leaves no confidence field, so such a card now takes the invented
 * pill instead of drawing none. The two narrative fields are dropped for good and never replaced:
 * this file still invents no reason code and no `Why this result?` sentence.
 *
 * A card that was already `ok` keeps everything the wire gave it, exactly as before.
 */
export const withDemoFallback = (
  fixture: IntelligenceFixture,
  // The tenant this card is being assembled for, threaded down from the data hook rather than read
  // off a module-level global: the fixture is per-card and the demo is per-tenant, so the only place
  // the two meet is the argument list.
  tenantName: string | null | undefined,
  layer?: DemoFallbackLayer,
  // The card's registry rows, for the LABEL a substituted segment set draws under. Structural, so
  // `MetricIdentity[]` passes without this file importing the registry.
  entries: { key: string; label: string }[] = []
): IntelligenceFixture => {
  if (!demoFallbackAllowed(tenantName) || !layer) {
    return fixture;
  }

  const card = DEMO_FALLBACK_VALUES[layer][fixture.cardKey];

  if (!card) {
    return fixture;
  }

  // A card still fetching has not failed; everything else is either `ok` (blank tiles filled, as
  // before) or a failure state the demo draws over. WHY the reading is missing stopped deciding this
  // on 2026-09-21 — see the removal of "never paint over a refusal" at `DEMO_FALLBACK_VALUES`.
  if (fixture.state.kind === 'loading') {
    return fixture;
  }

  const rescued = isRescuedState(fixture.state.kind);

  const byKey = card.metrics ?? {};
  const byText = card.texts ?? {};
  // The named exceptions to "a live reading always wins", and the ONLY ones. DEMO ONLY, 2026-09-21.
  const overridden = DEMO_LIVE_OVERRIDES[layer][fixture.cardKey] ?? [];

  const metrics = (
    card.readinessRows
      ? filledReadinessMetrics(fixture.metrics, card.readinessRows)
      : fixture.metrics
  ).map(metric => {
    if (hasReading(metric) && !overridden.includes(metric.key)) {
      return metric;
    }

    const value = byKey[metric.key];
    const text = byText[metric.key];

    return typeof value === 'number' || text !== undefined
      ? filledMetric(metric, value, text)
      : metric;
  });

  const distributions = card.distributions
    ? withDemoDistributions(
        fixture.distributions ?? {},
        card.distributions,
        key => entries.find(candidate => candidate.key === key)?.label
      )
    : undefined;

  const filledTile = metrics.some(
    (metric, index) => !hasReading(fixture.metrics[index]) && hasReading(metric)
  );

  /* A tile that was OVERWRITTEN rather than filled, which `filledTile` cannot see: its fixture tile
     already had a reading. Without this the card below would return the untouched fixture and throw
     the override away. DEMO ONLY, 2026-09-21. */
  const overriddenTile = metrics.some(
    (metric, index) => metric !== fixture.metrics[index] && hasReading(fixture.metrics[index])
  );

  /**
   * The invented pill, on a card that has none. DEMO ONLY, 2026-09-21.
   *
   * The guard is `!result.confidence` and not `result.confidence === undefined` deliberately: an
   * explicit `null` draws the `insufficient` badge over a card the demo has just filled, which says
   * the opposite of what the filled card shows. `assembleCard` only ever spreads a truthy
   * confidence, so this reaches a null only from a hand-built fixture.
   */
  const withDemoConfidence = (result: IntelligenceFixture): IntelligenceFixture => {
    if (!card.confidence || result.confidence) {
      return result;
    }

    /* Resolved exactly as `data/cardConfidence.ts` resolves the wire's own leading reason: code ->
       key -> copy, and nothing at all for a code the catalogue has no copy for. An entry with no
       `reasonCode` keeps the empty list every Layer 1 entry publishes today. */
    const copyKey = reasonCopyKey(
      `${TCustomerInsights}.ConfidenceGovernance.Reasons`,
      card.confidence.reasonCode
    );
    const { reasonCode, ...pill } = card.confidence;

    return { ...result, confidence: { ...pill, reasons: copyKey ? [i18n.t(copyKey)] : [] } };
  };

  // Nothing was filled, so the card has nothing new to show and keeps the state the wire put it in —
  // which is what leaves a Product Readiness card with no product selected on its own empty state.
  // A card ALREADY drawing its body still takes the pill: it has every tile it needs and the only
  // thing missing was the badge, which is the case on most of the cards `DEMO_CARD_CONFIDENCE` names.
  if (!filledTile && !overriddenTile && !distributions) {
    return fixture.state.kind === 'ok' ? withDemoConfidence(fixture) : fixture;
  }

  // The card has something to show now, so it must draw its body rather than a data-state block —
  // every non-`ok` state replaces the whole body (`components/Intelligence/InsightCard`).
  const state: CardDataState = { kind: 'ok' };
  const filled = { ...fixture, state, metrics, ...(distributions ? { distributions } : {}) };

  if (!rescued) {
    return withDemoConfidence(filled);
  }

  // `InsightCard` draws the pill for any resolved card carrying the field, and an explicit `null`
  // draws the `insufficient` badge rather than nothing — so the field is REMOVED, not nulled.
  const { confidence, whyThisResult, whyThisResultReasons, ...withoutGovernedNarrative } = filled;

  // The drop above left no confidence field at all, so a rescued card takes the invented pill. The
  // two narrative fields stay dropped: this file invents neither. DEMO ONLY, 2026-09-21.
  return withDemoConfidence(withoutGovernedNarrative);
};

/* The panels below stand on constants that were emptied when the placeholder readings came out. They
   are section-level drawings rather than tiles, so they cannot be filled through `withDemoFallback`;
   each is read by exactly one line of `sections/IndustrySection.tsx`. Same population as above:
   1,780 banked customers, 1,640 of them with classifiable spend, 1,455 carrying a cashflow model. */

/** SPEND COMPOSITION. The two halves divide to 1.00, which is the ratio O-BFSI-005 really answers. */
export const DEMO_BANKING_SPEND: {
  spendSplit: { label: string; value: number }[];
  categorizedCustomers?: number;
} = {
  spendSplit: [
    { label: 'Essential', value: 1252400 },
    { label: 'Discretionary', value: 1251000 }
  ],
  categorizedCustomers: 1640
};

/** The customers an income signal was detected for — 68% of the 1,780 banked. */
export const DEMO_BANKING_INCOME_COVERAGE: { detected?: number } = { detected: 1210 };

/** CASHFLOW HEALTH. Counts, not shares: 698/495/262 of 1,455 divide to the 48 / 34 / 18 drawn. */
export const DEMO_BANKING_CASHFLOW_HEALTH: { label: string; value: number }[] = [
  { label: 'Healthy positive cashflow', value: 698 },
  { label: 'Stable / watch', value: 495 },
  { label: 'Negative / stressed', value: 262 }
];

/** FORECAST SHORTFALL RISK. Each rate is its own count over the same 1,455 modelled customers. */
export const DEMO_BANKING_FORECAST_SHORTFALL: {
  label: string;
  customers: number;
  rate: number;
}[] = [
  { label: 'Next 7 days', customers: 122, rate: 8.4 },
  { label: 'Next 14 days', customers: 163, rate: 11.2 },
  { label: 'Next 30 days', customers: 212, rate: 14.6 }
];

/* TOP POSITIVE DRIVERS on Growth Planner Handoff MOVED on 2026-09-21 to
   `demoFallbackCustomerInsights.ts` (`growthOpportunities.handoffDrivers`), where the rest of the
   Layer 1 panel content now lives, and is WIRED — the user asked for the empty rails filled. It was
   held back until then because a named driver with an impact figure is a causal claim rather than a
   tile figure, and three suites asserted the rail stays empty "rather than ranking Lorem Ipsum";
   those suites now run with the flag off. Not left duplicated here: two driver lists for one rail
   is the kind of drift this whole layer has to avoid. */

/* ---------------------------------------------------------------------------------------------
   CUSTOMER 360 `View More` ROW SETS

   A Customer 360 overlay never fetches: `customer360/breakdowns/Customer360Breakdown.tsx` reprints
   the block the card already drew, handed every row instead of the first three, and
   `breakdowns/inlineRows.ts` draws the link ONLY above `C360_INLINE_ROW_LIMIT` (3). So a block
   standing on an empty constant has no link at all and its overlay cannot be opened — which is why
   these are row sets rather than tile figures, and why each carries at least FOUR rows.

   Customer grain, one customer, and every figure is consistent with the tiles `DEMO_FALLBACK_VALUES.
   customer_360` puts on the same card:
     - financial_health   income_monthly 4,820 / account_balance 7,640
     - risk_unusual_activity  affected_events_30d 6 (the row count below, raised from 3 for the link)
     - cash_usage_dependency  impact_on_insights 5 (likewise), cash_withdrawals 1,450
     - rewards_loyalty_activity  18 issued / 11 redeemed / 3 expired / 2 reversed
   Nothing here carries a confidence score, a confidence band, a reason code, a `calculation_status`
   or a `Why this result?` sentence: those stay governed and stay as the wire sent them.
   --------------------------------------------------------------------------------------------- */

/** One `Unresolved Issues` row on Customer Experience; the panel's `Open` badge counts them. */
export const DEMO_C360_UNRESOLVED_ISSUES: {
  key: string;
  severity: Customer360IssueSeverity;
  tone: 'positive' | 'warning' | 'negative' | 'info' | 'accent';
  text: string;
}[] = [
  {
    key: 'demo_issue_fulfilment',
    severity: 'High',
    tone: 'negative',
    text: 'Reward fulfilment ran past the promised window'
  },
  {
    key: 'demo_issue_points',
    severity: 'High',
    tone: 'negative',
    text: 'Points for a qualifying transaction are not credited yet'
  },
  {
    key: 'demo_issue_checkout',
    severity: 'Medium',
    tone: 'warning',
    text: 'Checkout failed twice in the app before going through'
  },
  {
    key: 'demo_issue_survey',
    severity: 'Medium',
    tone: 'warning',
    text: 'Survey response flagged for follow-up, no contact made'
  },
  {
    key: 'demo_issue_statement',
    severity: 'Low',
    tone: 'info',
    text: 'Statement query raised, awaiting an agent reply'
  }
];

/** The shape every timeline row set below satisfies — `components/Intelligence/EventTimeline`. */
interface DemoTimelineEvent {
  id: string;
  timestamp: string;
  type: string;
  status?: { label: string; tone: 'positive' | 'warning' | 'negative' | 'info' | 'neutral' };
  subject?: string;
  amount?: string;
  source?: string;
  detail?: string;
}

/**
 * RECENT EVIDENCE on Customer Experience. `Sentiment` is the row's own word and `Impact` its own
 * movement; neither is a confidence band. Two negative interactions sit behind the two High issues
 * above, so the panel and the issue list tell one story.
 */
export const DEMO_C360_RECENT_EVIDENCE: DemoTimelineEvent[] = [
  {
    id: 'demo-ce-1',
    timestamp: '12 May, 09:12 AM',
    source: 'Survey',
    type: 'NPS Response',
    detail: 'Rated 4 of 10 after a delayed redemption',
    status: { label: 'Negative', tone: 'negative' },
    amount: '-6'
  },
  {
    id: 'demo-ce-2',
    timestamp: '10 May, 04:45 PM',
    source: 'Support',
    type: 'Complaint',
    detail: 'Points for a qualifying transaction not credited',
    status: { label: 'Negative', tone: 'negative' },
    amount: '-4'
  },
  {
    id: 'demo-ce-3',
    timestamp: '08 May, 11:02 AM',
    source: 'App',
    type: 'Reward Redemption',
    detail: 'Voucher redeemed without assistance',
    status: { label: 'Positive', tone: 'positive' },
    amount: '+3'
  },
  {
    id: 'demo-ce-4',
    timestamp: '05 May, 06:30 PM',
    source: 'Campaign',
    type: 'Offer Response',
    detail: 'Opened and claimed the weekend cashback offer',
    status: { label: 'Positive', tone: 'positive' },
    amount: '+2'
  },
  {
    id: 'demo-ce-5',
    timestamp: '02 May, 01:18 PM',
    source: 'Support',
    type: 'Statement Query',
    detail: 'Query logged, awaiting an agent reply',
    status: { label: 'Neutral', tone: 'neutral' },
    amount: '0'
  }
];

/**
 * SPEND BREAKDOWN on Financial Health, as fractions of monthly outflow. The four shares total 1.00,
 * and they are the same 45 / 30 / 15 / 10 split the card's own `spend_distribution` caption states,
 * so the donut, its legend and the caption above it cannot disagree.
 */
export const DEMO_C360_SPEND_BREAKDOWN: { label: string; pct: number }[] = [
  { label: 'Essentials', pct: 0.45 },
  { label: 'Discretionary', pct: 0.3 },
  { label: 'Financial', pct: 0.15 },
  { label: 'Other', pct: 0.1 }
];

/**
 * DEBT OBLIGATIONS — Item / Amount / EMI. The five EMIs total $862, which is 17.9% of the $4,820
 * `income_monthly` tile on the same card.
 */
export const DEMO_C360_DEBT_ROWS: string[][] = [
  ['Home Loan', '$52,000', '$430'],
  ['Auto Loan', '$9,400', '$186'],
  ['Personal Loan', '$5,200', '$102'],
  ['Credit Card', '$2,450', '$98'],
  ['Consumer Durable Loan', '$1,150', '$46']
];

/** INCOME SOURCE — Item / Amount. The four credits total exactly the $4,820 `income_monthly` tile. */
export const DEMO_C360_INCOME_SOURCE_ROWS: string[][] = [
  ['Salary Credit', '$3,900'],
  ['Rental Income', '$520'],
  ['Interest & Dividends', '$215'],
  ['Other Credits', '$185']
];

/** A marker row on Financial Health — a sentence and the tone it is drawn in, nothing else. */
interface DemoMarkerItem {
  label: string;
  tone: 'positive' | 'warning' | 'negative' | 'neutral';
}

/** FINANCIAL HEALTH INSIGHTS. Each line restates a figure already on the card. */
export const DEMO_C360_HEALTH_INSIGHTS: DemoMarkerItem[] = [
  { label: 'Salary credited on schedule for six consecutive months', tone: 'positive' },
  { label: 'Balance stayed above the low-balance threshold all period', tone: 'positive' },
  { label: 'Essential spend held at 45% of monthly outflow', tone: 'neutral' },
  { label: 'EMI obligations run at 17.9% of monthly income', tone: 'warning' }
];

/** OPPORTUNITIES & RISKS, on the same card and the same four figures. */
export const DEMO_C360_OPPORTUNITIES_RISKS: DemoMarkerItem[] = [
  { label: 'Monthly surplus supports a recurring deposit', tone: 'positive' },
  { label: 'Eligible for an auto-loan top-up at the current EMI load', tone: 'positive' },
  { label: 'Credit card balance has risen in each of the last three months', tone: 'warning' },
  { label: 'Buffer covers under two months of outflow', tone: 'negative' }
];

/**
 * RECENT RELEVANT TRANSACTIONS on Opportunities & Product Interest — Date / Amount / Merchant /
 * Category / Status. `type` is the merchant column and `subject` the category, per the card's own
 * column map.
 */
export const DEMO_C360_RECENT_TRANSACTIONS: DemoTimelineEvent[] = [
  {
    id: 'demo-tx-1',
    timestamp: '12 May, 07:41 PM',
    type: 'FairPrice',
    subject: 'Groceries',
    amount: '$186.40',
    status: { label: 'Posted', tone: 'positive' }
  },
  {
    id: 'demo-tx-2',
    timestamp: '11 May, 08:16 PM',
    type: 'Shell',
    subject: 'Fuel',
    amount: '$72.10',
    status: { label: 'Posted', tone: 'positive' }
  },
  {
    id: 'demo-tx-3',
    timestamp: '09 May, 01:05 PM',
    type: 'Grab',
    subject: 'Transport',
    amount: '$24.80',
    status: { label: 'Posted', tone: 'positive' }
  },
  {
    id: 'demo-tx-4',
    timestamp: '07 May, 10:22 AM',
    type: 'Amazon',
    subject: 'Retail',
    amount: '$118.95',
    status: { label: 'Posted', tone: 'positive' }
  },
  {
    id: 'demo-tx-5',
    timestamp: '04 May, 09:58 PM',
    type: 'Netflix',
    subject: 'Subscriptions',
    amount: '$17.98',
    status: { label: 'Pending', tone: 'warning' }
  }
];

/**
 * MODEL FACTORS on the same card. Directions, not contributions: no factor carries a weight, a
 * score or a share, so nothing here states how the model reached its reading.
 */
export const DEMO_C360_MODEL_FACTORS: {
  label: string;
  tone: 'positive' | 'warning' | 'negative' | 'info' | 'accent';
}[] = [
  { label: 'Spend velocity rising', tone: 'positive' },
  { label: 'Category breadth widening', tone: 'positive' },
  { label: 'Repayment record clean', tone: 'positive' },
  { label: 'Balance volatility increasing', tone: 'negative' },
  { label: 'Engagement recency slipping', tone: 'negative' }
];

/**
 * RESTRICTION HISTORY on Contact Restrictions — Date / State / Reason, drawn `statusVariant="text"`.
 * The last row is the state the card's own Contactability pill shows, so the history ends where the
 * card starts.
 */
export const DEMO_C360_RESTRICTION_HISTORY: DemoTimelineEvent[] = [
  {
    id: 'demo-rh-1',
    timestamp: '12 May, 12:00 AM',
    type: 'Frequency Cap',
    status: { label: 'Restricted', tone: 'warning' },
    detail: 'Frequency cap reached - 3 of 3 in the last 7 days'
  },
  {
    id: 'demo-rh-2',
    timestamp: '04 May, 12:00 AM',
    type: 'Frequency Cap',
    status: { label: 'Open', tone: 'positive' },
    detail: 'Cap window reset'
  },
  {
    id: 'demo-rh-3',
    timestamp: '22 Apr, 12:00 AM',
    type: 'Consent',
    status: { label: 'Open', tone: 'positive' },
    detail: 'Marketing consent re-confirmed in app'
  },
  {
    id: 'demo-rh-4',
    timestamp: '09 Apr, 12:00 AM',
    type: 'Channel',
    status: { label: 'Restricted', tone: 'warning' },
    detail: 'SMS paused at the customer request'
  }
];

/**
 * LOW CONFIDENCE TRANSACTIONS on Cash Usage — Date / Merchant / Amount / Confidence / Reason.
 * Every row is `Low` because that is the block's own definition rather than a score read off
 * anything: no figure is put in that column. The payees are merchants, never a person or a handle.
 */
export const DEMO_C360_LOW_CONFIDENCE_TRANSACTIONS: DemoTimelineEvent[] = [
  {
    id: 'demo-lc-1',
    timestamp: '12 May, 09:31 PM',
    type: 'POS 4471 *MASKED*',
    amount: '$64.00',
    status: { label: 'Low', tone: 'warning' },
    detail: 'Gateway descriptor masked'
  },
  {
    id: 'demo-lc-2',
    timestamp: '10 May, 02:07 PM',
    type: 'SQ *MERCHANT',
    amount: '$128.50',
    status: { label: 'Low', tone: 'warning' },
    detail: 'Merchant alias not in the catalogue'
  },
  {
    id: 'demo-lc-3',
    timestamp: '06 May, 11:44 AM',
    type: 'INTL RETAIL 8820',
    amount: '$212.75',
    status: { label: 'Low', tone: 'warning' },
    detail: 'Cross-border descriptor'
  },
  {
    id: 'demo-lc-4',
    timestamp: '03 May, 07:19 PM',
    type: 'PMT GATEWAY 02',
    amount: '$45.20',
    status: { label: 'Low', tone: 'warning' },
    detail: 'Truncated narration'
  }
];

/**
 * IMPACT ON INSIGHTS on Cash Usage — a label and its rating pill, no timestamp column. FIVE rows,
 * which is the `impact_on_insights` tile beside them.
 */
export const DEMO_C360_IMPACT_ON_INSIGHTS: DemoTimelineEvent[] = [
  {
    id: 'demo-ii-1',
    timestamp: '',
    type: 'Spend Category Accuracy',
    status: { label: 'Medium', tone: 'warning' }
  },
  {
    id: 'demo-ii-2',
    timestamp: '',
    type: 'Merchant Affinity',
    status: { label: 'Low', tone: 'negative' }
  },
  {
    id: 'demo-ii-3',
    timestamp: '',
    type: 'Income Detection',
    status: { label: 'High', tone: 'positive' }
  },
  {
    id: 'demo-ii-4',
    timestamp: '',
    type: 'Cashflow Forecast',
    status: { label: 'Medium', tone: 'warning' }
  },
  {
    id: 'demo-ii-5',
    timestamp: '',
    type: 'Budget Adherence',
    status: { label: 'Low', tone: 'negative' }
  }
];

/**
 * UNCLASSIFIED REASONS on Transaction Data Quality. Counts, drawn without shares
 * (`printShare={false}`): 971 unclassified transactions of 3,407 read is the 28.5% left over by the
 * 71.5% `merchant_alias_match_rate` tile on the same card, and the five reasons total that 971.
 */
export const DEMO_C360_UNCLASSIFIED_REASONS: { label: string; value: string }[] = [
  { label: 'Merchant alias not in catalogue', value: '412' },
  { label: 'Gateway descriptor masked', value: '268' },
  { label: 'Cross-border descriptor', value: '141' },
  { label: 'Truncated narration', value: '96' },
  { label: 'Duplicate / reversal pairing', value: '54' }
];

/**
 * RECENT ENTITLEMENT & REVERSAL TIMELINE on Rewards & Loyalty. Exactly TWO of the six rows are
 * reversals, which is the 2 `C-REV-001` reversals the card's own tiles report, and the two reversed
 * amounts total the 1,250 points its `reversed_entitlement_summary` tile prints.
 */
export const DEMO_C360_TIMELINE_EVENTS: DemoTimelineEvent[] = [
  {
    id: 'demo-tl-1',
    timestamp: '12 May, 06:40 PM',
    type: 'Redeemed',
    subject: 'Amazon Pay Voucher',
    status: { label: 'Completed', tone: 'positive' },
    amount: '-800 pts',
    source: 'App',
    detail: 'Voucher issued to the customer wallet'
  },
  {
    id: 'demo-tl-2',
    timestamp: '11 May, 08:16 PM',
    type: 'Reversal',
    subject: 'Fuel Cashback',
    status: { label: 'Reversed', tone: 'negative' },
    amount: '-450 pts',
    source: 'Provider',
    // PLA-8828 FE-C13: was 'Provider cancellation', a Product-rejected reason. Substituted rather
    // than deleted so the timeline keeps its row count; the event itself is not the problem.
    detail: 'Duplicate issuance'
  },
  {
    id: 'demo-tl-3',
    timestamp: '09 May, 10:05 AM',
    type: 'Issued',
    subject: 'Weekend Cashback',
    status: { label: 'Active', tone: 'positive' },
    amount: '+1,200 pts',
    source: 'Campaign',
    detail: 'Weekend spend campaign'
  },
  {
    id: 'demo-tl-4',
    timestamp: '06 May, 03:22 PM',
    type: 'Reversal',
    subject: 'Grocery Bonus',
    status: { label: 'Reversed', tone: 'negative' },
    amount: '-800 pts',
    source: 'Merchant',
    // PLA-8828 FE-C13: was 'Underlying transaction refunded', a Product-rejected reason.
    detail: 'Manual correction'
  },
  {
    id: 'demo-tl-5',
    timestamp: '02 May, 09:12 AM',
    type: 'Expired',
    subject: 'Welcome Bonus',
    status: { label: 'Expired', tone: 'neutral' },
    amount: '-300 pts',
    source: 'System',
    detail: 'Validity window closed'
  },
  {
    id: 'demo-tl-6',
    timestamp: '28 Apr, 07:48 PM',
    type: 'Issued',
    subject: 'Referral Reward',
    status: { label: 'Active', tone: 'positive' },
    amount: '+500 pts',
    source: 'Referral',
    detail: 'Referred customer completed first transaction'
  }
];

/**
 * RECENT AFFECTED EVENTS on Risk & Unusual Activity — Date / Type / Channel / Amount / Status /
 * Risk Reason / Severity. SIX rows, which is the `affected_events_30d` tile beside them, and every
 * one falls inside that 30-day window. `status` is the event's own disposition and `subject` its
 * severity word; neither is a confidence band, and no row carries a fraud score.
 */
export const DEMO_C360_AFFECTED_EVENTS: DemoTimelineEvent[] = [
  {
    id: 'demo-ae-1',
    timestamp: '12 May, 11:52 PM',
    type: 'Velocity Breach',
    source: 'Card',
    amount: '$482.00',
    status: { label: 'Flagged', tone: 'negative' },
    detail: 'Six card transactions inside ten minutes',
    subject: 'High'
  },
  {
    id: 'demo-ae-2',
    timestamp: '10 May, 02:07 PM',
    type: 'Amount Outlier',
    source: 'Card',
    amount: '$1,240.00',
    status: { label: 'Under review', tone: 'warning' },
    detail: 'Ticket size well above the usual band',
    subject: 'High'
  },
  {
    id: 'demo-ae-3',
    timestamp: '07 May, 08:33 AM',
    type: 'Device Change',
    source: 'App',
    amount: '-',
    status: { label: 'Cleared', tone: 'positive' },
    detail: 'Sign-in from a new device, verified by OTP',
    subject: 'Medium'
  },
  {
    id: 'demo-ae-4',
    timestamp: '04 May, 09:58 PM',
    type: 'Duplicate Activity',
    source: 'Card',
    amount: '$96.40',
    status: { label: 'Cleared', tone: 'positive' },
    detail: 'Two identical authorisations, one settled',
    subject: 'Low'
  },
  {
    id: 'demo-ae-5',
    timestamp: '29 Apr, 06:14 PM',
    type: 'Callback Mismatch',
    source: 'Gateway',
    amount: '$212.75',
    status: { label: 'Under review', tone: 'warning' },
    detail: 'Gateway callback did not match the authorisation',
    subject: 'Medium'
  },
  {
    id: 'demo-ae-6',
    timestamp: '24 Apr, 01:41 PM',
    type: 'IP Risk',
    source: 'Web',
    amount: '-',
    status: { label: 'Cleared', tone: 'positive' },
    detail: 'Sign-in from a flagged network range',
    subject: 'Low'
  }
];

export default withDemoFallback;
